import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import EcoPageHeader from "../../../components/eco-page-header/EcoPageHeader";
import EcoConfirmDialog from "../../../components/eco-ui/EcoConfirmDialog";
import type { RootState } from "../../../store/store";

import { getStoresPaginatedApi, StorePublic } from "../../store/storeApi";

import EmployeeStatusBadge from "../components/EmployeeStatusBadge";
import RecurringDeductionsCard from "../components/RecurringDeductionsCard";
import VariableHistoryCard from "../components/VariableHistoryCard";
import ContractBenefitsCard from "../components/ContractBenefitsCard";
import CredentialStatusBadge from "../components/CredentialStatusBadge";
import EmployeePhoto from "../components/EmployeePhoto";
import {
  disableCredentialApi,
  getCredentialStatusApi,
  provisionCredentialApi,
  reactivateCredentialApi,
  resetCredentialApi,
  CREDENTIAL_EVENT_LABELS,
  MIN_PROVISIONAL_PASSWORD_LENGTH,
  DpCredentialStatus,
} from "../api/credentialsApi";
import DependentRowsEditor, {
  DependentRow,
  emptyDependentRow,
  isBlankDependentRow,
  toDependentPayload,
  validateDependentRows,
} from "../components/DependentRowsEditor";
import {
  createDependentsBulkApi,
  deleteDependentApi,
  deleteEmployeeApi,
  getEmployeeApi,
  getRemunerationSummaryApi,
  getUserSuggestionApi,
  linkEmployeeUserApi,
  unlinkEmployeeUserApi,
  updateDependentApi,
  DpEmployeeDependent,
  DpEmployeeDetail,
  DpRemunerationSummary,
} from "../api/employeesApi";
import {
  addContractVersionApi,
  createBenefitElectionApi,
  createContractApi,
  deleteBenefitElectionApi,
  deleteContractVersionApi,
  listContractVersionChangesApi,
  updateBenefitElectionApi,
  updateContractVersionApi,
  BenefitElectionPayload,
  ContractVersionPayload,
  DpBenefitElection,
  DpContractVersion,
  DpContractVersionChange,
  DpEmployeeContract,
} from "../api/contractsApi";
import ProbationEditModal from "../components/ProbationEditModal";
import ReasonModal from "../components/ReasonModal";
import TerminationPaymentModal from "../components/TerminationPaymentModal";
import { probation45Plus45, probationStateOf, PROBATION_STATE_LABELS } from "../utils/probation";
import {
  listCostCentersApi,
  listEmployersApi,
  listPositionsApi,
  DpCostCenter,
  DpEmployer,
  DpPosition,
} from "../api/settingsApi";
import {
  ALLOWANCE_BASE_LABELS,
  ALLOWANCE_BASE_SHORT_LABELS,
  BANK_ACCOUNT_TYPE_LABELS,
  CONTRACT_CHANGE_REASON_LABELS,
  CONTRACT_CHANGE_REASONS_SELECTABLE,
  CONTRACT_TYPE_LABELS,
  CONTRACT_TYPES_WITH_FIXED_TERM,
  DEPENDENT_RELATIONSHIP_LABELS,
  DpAllowanceBase,
  DpContractChangeReason,
  DpContractType,
  DpSalaryBasis,
  GENDER_LABELS,
  MARITAL_STATUS_LABELS,
  MAX_TRANSPORT_VOUCHER_EMPLOYEE_PCT,
  SALARIO_FAMILIA_REASON_LABELS,
  SALARY_BASIS_LABELS,
} from "../api/dpTypes";
import {
  formatCnpj,
  formatCpf,
  formatCurrency,
  formatDateOnly,
  formatHours,
  orDash,
} from "../utils/format";
import { DP_EMPLOYEES_PAGE, DP_EMPLOYEE_EDIT_PAGE, DP_PAGE } from "../routes";
import ProvisionalPasswordModal, {
  portalUrlCopiada,
  portalUrlExibida,
} from "../components/ProvisionalPasswordModal";
import EmployeeDocumentsTab from "../components/EmployeeDocumentsTab";
import EmployeeProcessesTab from "../components/EmployeeProcessesTab";
import EmployeeVacationsTab from "../components/EmployeeVacationsTab";
import EmployeeProvisionsTab from "../components/EmployeeProvisionsTab";
import EmployeeLeavesTab from "../components/EmployeeLeavesTab";
import EmployeeScheduleTab from "../components/EmployeeScheduleTab";
import EmployeeTimekeepingTab from "../components/EmployeeTimekeepingTab";
import { isDpFromToken } from "../../../utils/authToken";
import { LEAVE_TYPE_LABELS } from "../api/leavesApi";
import { useGoBack } from "../../../hooks/useGoBack";
import "./Dp.css";
import "./DpEco.css";

type Tab = "dados" | "dependentes" | "contrato" | "escala" | "ponto" | "documentos" | "processos" | "ferias" | "provisoes" | "afastamentos" | "acesso";
const TABS: Tab[] = ["dados", "dependentes", "contrato", "escala", "ponto", "documentos", "processos", "ferias", "provisoes", "afastamentos", "acesso"];

/** Data e hora de um instante (não é data civil — aqui o fuso local está certo). */
const formatDateTime = (value?: string | null): string => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString("pt-BR");
};

const emptyVersion = {
  effectiveFrom: "",
  changeReason: "REAJUSTE" as DpContractChangeReason,
  positionId: "",
  salaryAmount: "",
  salaryBasis: "MENSAL" as DpSalaryBasis,
  weeklyHours: "44",
  monthlyHours: "220",
  costCenterId: "",
  storeId: "",
  /** Adicionais: percentual vazio = não informado; "0" = não recebe. */
  insalubrityPercent: "",
  insalubrityBase: "" as DpAllowanceBase | "",
  insalubrityBaseAmount: "",
  hazardPercent: "",
  hazardBase: "" as DpAllowanceBase | "",
  hazardBaseAmount: "",
  /** Atividade complementar: só o percentual — a base é sempre o salário contratual. */
  complementaryActivityPercent: "",
  notes: "",
};

type VersionForm = typeof emptyVersion;
type AllowancePrefix = "insalubrity" | "hazard";
const ALLOWANCE_LABELS: Record<AllowancePrefix, string> = {
  insalubrity: "Insalubridade",
  hazard: "Periculosidade",
};

/** Na admissão o padrão é "não recebe" (0%): o operador vê e confirma. */
const emptyAdmissionVersion: VersionForm = {
  ...emptyVersion,
  insalubrityPercent: "0",
  hazardPercent: "0",
  complementaryActivityPercent: "0",
};

const emptyBenefit = {
  effectiveFrom: "",
  salaryAdvanceOptIn: false,
  salaryAdvancePercent: "40",
  transportVoucherOptIn: false,
  transportVoucherDailyAmount: "",
  transportVoucherEmployeePct: String(MAX_TRANSPORT_VOUCHER_EMPLOYEE_PCT),
  mealVoucherOptIn: false,
  mealVoucherDailyAmount: "",
  mealVoucherEmployeePct: "",
  foodVoucherOptIn: false,
  foodVoucherAmount: "",
  foodVoucherMode: "POR_DIA" as "POR_DIA" | "MENSAL",
  foodVoucherEmployeePct: "",
  motorcycleRentalOptIn: false,
  motorcycleRentalAmount: "",
  fuelVoucherOptIn: false,
  fuelVoucherAmount: "",
  fuelVoucherMode: "POR_DIA" as "POR_DIA" | "MENSAL",
  notes: "",
};

const decimalToInput = (value?: string | null): string =>
  value === null || value === undefined ? "" : String(Number(value));

const formatPercent = (value?: string | number | null): string =>
  `${Number(value).toLocaleString("pt-BR", { maximumFractionDigits: 2 })}%`;

/** "20% s/ salário mínimo" — como o adicional está no contrato (condição, não valor). */
const describeAllowance = (
  percent: string | null,
  base: DpAllowanceBase | null,
  baseAmount: string | null
): string => {
  if (percent === null || percent === undefined) return "não informado";
  if (Number(percent) === 0) return "não recebe";
  if (!base) return formatPercent(percent);
  const suffix = base === "VALOR_INFORMADO" && baseAmount ? ` (${formatCurrency(baseAmount)})` : "";
  return `${formatPercent(percent)} s/ ${ALLOWANCE_BASE_SHORT_LABELS[base]}${suffix}`;
};

/** Valida os adicionais do formulário e devolve os campos do payload, ou a mensagem de erro. */
const buildAllowancePayload = (form: VersionForm): Partial<ContractVersionPayload> | string => {
  const payload: Partial<ContractVersionPayload> = {};

  for (const prefix of ["insalubrity", "hazard"] as AllowancePrefix[]) {
    const label = ALLOWANCE_LABELS[prefix];
    const raw = form[`${prefix}Percent` as const].trim();
    if (!raw) continue; // não informado

    const percent = Number(raw);
    if (!Number.isFinite(percent) || percent < 0 || percent > 100) {
      return `${label}: o percentual deve estar entre 0 e 100.`;
    }
    payload[`${prefix}Percent` as const] = percent;
    if (percent === 0) continue;

    const base = form[`${prefix}Base` as const];
    if (!base) return `${label}: informe a base de cálculo.`;
    payload[`${prefix}Base` as const] = base;

    if (base === "VALOR_INFORMADO") {
      const amount = Number(form[`${prefix}BaseAmount` as const]);
      if (!Number.isFinite(amount) || amount <= 0) return `${label}: informe o valor da base.`;
      payload[`${prefix}BaseAmount` as const] = amount;
    }
  }

  const complementary = form.complementaryActivityPercent.trim();
  if (complementary) {
    const percent = Number(complementary);
    if (!Number.isFinite(percent) || percent < 0 || percent > 100) {
      return "Atividade complementar: o percentual deve estar entre 0 e 100.";
    }
    payload.complementaryActivityPercent = percent;
  }

  return payload;
};

/** "10% s/ salário contratual" — a base da atividade complementar é fixa. */
const describeComplementaryActivity = (percent: string | null): string =>
  describeAllowance(percent, "SALARIO_CONTRATUAL", null);

const describeAdvance = (e: DpBenefitElection): string =>
  e.salaryAdvanceOptIn && e.salaryAdvancePercent ? formatPercent(e.salaryAdvancePercent) : "não optante";

const describeVoucher = (optIn: boolean, daily: string | null, pct: string | null): string =>
  optIn && daily
    ? `${formatCurrency(daily)}/dia${pct !== null ? ` · participação ${formatPercent(pct)}` : ""}`
    : "não optante";

/** VA: `null` = não informado (eleição anterior ao VA); `false` = não optante. */
const describeFoodVoucher = (e: DpBenefitElection): string => {
  if (e.foodVoucherOptIn === null || e.foodVoucherOptIn === undefined) return "não informado";
  if (!e.foodVoucherOptIn || !e.foodVoucherAmount) return "não optante";
  const amount = formatCurrency(e.foodVoucherAmount) + (e.foodVoucherMode === "MENSAL" ? "/mês" : "/dia");
  return e.foodVoucherEmployeePct !== null
    ? amount + " · participação " + formatPercent(e.foodVoucherEmployeePct)
    : amount;
};

/** Aluguel de moto: `null` = não informado (opção anterior a 20/10/2026); `false` = não optante. */
const describeMotorcycleRental = (e: DpBenefitElection): string => {
  if (e.motorcycleRentalOptIn === null || e.motorcycleRentalOptIn === undefined) return "não informado";
  return e.motorcycleRentalOptIn && e.motorcycleRentalAmount ? formatCurrency(e.motorcycleRentalAmount) + "/mês" : "não optante";
};

/** Vale-combustível: por dia trabalhado ou mensal; `null` = não informado. */
const describeFuelVoucher = (e: DpBenefitElection): string => {
  if (e.fuelVoucherOptIn === null || e.fuelVoucherOptIn === undefined) return "não informado";
  if (!e.fuelVoucherOptIn || !e.fuelVoucherAmount) return "não optante";
  return formatCurrency(e.fuelVoucherAmount) + (e.fuelVoucherMode === "MENSAL" ? "/mês" : "/dia");
};

const dependentToRow = (d: DpEmployeeDependent): DependentRow => ({
  ...emptyDependentRow(),
  fullName: d.fullName,
  birthDate: d.birthDate.slice(0, 10),
  relationship: d.relationship,
  cpf: d.cpf ? formatCpf(d.cpf) : "",
  isIrDependent: d.isIrDependent,
  isSalarioFamiliaEligible: d.isSalarioFamiliaEligible,
  hasDisability: d.hasDisability,
  isUniversityStudent: d.isUniversityStudent,
});

const emptyContract = {
  employerId: "",
  contractType: "CLT_INDETERMINADO" as DpContractType,
  admissionDate: "",
  probationFirstEndDate: "",
  probationEndDate: "",
  fixedTermEndDate: "",
  registrationNumber: "",
  notes: "",
};

export default function EmployeeDetailPage() {
  const navigate = useNavigate();
  const goBack = useGoBack(DP_EMPLOYEES_PAGE());
  const { id } = useParams<{ id: string }>();
  const token = useSelector((state: RootState) => state.auth.accessToken);

  const [employee, setEmployee] = useState<DpEmployeeDetail | null>(null);
  const [loading, setLoading] = useState(true);
  /**
   * A aba fica na URL (`?aba=`): um link pode abrir a ficha direto na aba certa
   * (ex.: "Documentos a vencer" → documento), e Voltar/F5 preservam a aba.
   * Trocar de aba usa `replace` para não encher o histórico — o Voltar sai da ficha.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const tab: Tab = TABS.includes(searchParams.get("aba") as Tab) ? (searchParams.get("aba") as Tab) : "dados";
  const highlightedDocumentId = tab === "documentos" ? searchParams.get("documento") : null;
  const setTab = useCallback(
    (next: Tab) => setSearchParams(next === "dados" ? {} : { aba: next }, { replace: true }),
    [setSearchParams]
  );
  const [saving, setSaving] = useState(false);

  const [employers, setEmployers] = useState<DpEmployer[]>([]);
  const [positions, setPositions] = useState<DpPosition[]>([]);
  const [costCenters, setCostCenters] = useState<DpCostCenter[]>([]);
  const [stores, setStores] = useState<StorePublic[]>([]);

  const [dependentModal, setDependentModal] = useState(false);
  const [dependentRows, setDependentRows] = useState<DependentRow[]>(() => [emptyDependentRow()]);
  /** Dependente em edição (modal de uma linha só). */
  const [editingDependent, setEditingDependent] = useState<{ id: string; row: DependentRow } | null>(
    null
  );

  /** Remuneração de referência e conferência do salário-família, calculadas no backend. */
  const [summary, setSummary] = useState<DpRemunerationSummary | null>(null);

  const [benefitModal, setBenefitModal] = useState<string | null>(null);
  /** Registro em correção (ADR 0026); null = registro novo a partir de uma data. */
  const [benefitEditing, setBenefitEditing] = useState<DpBenefitElection | null>(null);
  const [benefitToDelete, setBenefitToDelete] = useState<{ contractId: string; election: DpBenefitElection } | null>(
    null
  );
  const [benefitForm, setBenefitForm] = useState(emptyBenefit);

  const [contractModal, setContractModal] = useState(false);
  const [probationContract, setProbationContract] = useState<DpEmployeeContract | null>(null);
  /** Contrato rescindido cujo demonstrativo de rescisão (F2c) está aberto. */
  const [terminationContractId, setTerminationContractId] = useState<string | null>(null);
  const [contractForm, setContractForm] = useState(emptyContract);
  const [initialVersionForm, setInitialVersionForm] = useState(emptyAdmissionVersion);

  const [versionModal, setVersionModal] = useState<string | null>(null);
  const [versionForm, setVersionForm] = useState(emptyVersion);
  /** Versão em correção (ADR 0030); null = alteração contratual nova. */
  const [versionEditing, setVersionEditing] = useState<DpContractVersion | null>(null);
  const [versionEditReason, setVersionEditReason] = useState("");
  const [versionChanges, setVersionChanges] = useState<DpContractVersionChange[] | null>(null);
  const [versionToDelete, setVersionToDelete] = useState<{ contractId: string; version: DpContractVersion } | null>(
    null
  );

  const [suggestion, setSuggestion] = useState<{
    user: { id: string; nomeCompleto: string; cpf: string; userType: string } | null;
    alreadyLinked: boolean;
  } | null>(null);

  // ── Acesso ao app (A.2) ───────────────────────────────────────────────────
  const [credential, setCredential] = useState<DpCredentialStatus | null>(null);
  const [credentialLoading, setCredentialLoading] = useState(false);
  /**
   * Senha provisória em claro. Vive SÓ neste estado, enquanto o modal está
   * aberto: o backend a devolve uma única vez e depois guarda apenas o hash.
   */
  const [provisionalPassword, setProvisionalPassword] = useState<string | null>(null);
  const [disableModal, setDisableModal] = useState(false);
  const [disableReason, setDisableReason] = useState("");
  /**
   * Modal em que o DP DIGITA a senha provisória, para criar o acesso ou reemitir.
   * A senha é ditada no balcão e digitada num celular por quem não é técnico —
   * por isso quem escolhe é o DP, e não um sorteio.
   */
  const [passwordModal, setPasswordModal] = useState<"provision" | "reset" | null>(null);
  const [provisionalInput, setProvisionalInput] = useState("");

  const load = useCallback(async () => {
    if (!token || !id) return;
    setLoading(true);
    try {
      setEmployee(await getEmployeeApi(token, id));
    } catch (error: any) {
      toast.error(error?.message || "Erro ao carregar o colaborador.");
      navigate(DP_EMPLOYEES_PAGE(), { replace: true });
    } finally {
      setLoading(false);
    }
  }, [token, id, navigate]);

  /**
   * A ficha (`GET /dp/employees/:id`) já traz um resumo do acesso, mas não a
   * trilha de eventos — ela só é buscada quando a aba é aberta, para não pesar
   * o carregamento de quem só quer ver os dados do colaborador.
   */
  const loadCredential = useCallback(async () => {
    if (!token || !id) return;
    setCredentialLoading(true);
    try {
      setCredential(await getCredentialStatusApi(token, id));
    } catch (error: any) {
      toast.error(error?.message || "Erro ao carregar a situação do acesso.");
    } finally {
      setCredentialLoading(false);
    }
  }, [token, id]);

  useEffect(() => {
    if (tab === "acesso") loadCredential();
  }, [tab, loadCredential]);

  const runCredentialAction = async (
    acao: () => Promise<DpCredentialStatus & { provisionalPassword?: string }>,
    sucesso: string
  ) => {
    setSaving(true);
    try {
      const data = await acao();
      setCredential(data);
      if (data.provisionalPassword) setProvisionalPassword(data.provisionalPassword);
      toast.success(sucesso);
      // A ficha carrega o resumo do acesso; recarregar mantém o badge do
      // cabeçalho coerente com o que a aba acabou de mostrar.
      await load();
    } catch (error: any) {
      toast.error(error?.message || "Não foi possível concluir a ação.");
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    load();
  }, [load]);

  // A cada recarga da ficha (admissão, versão, benefício, dependente) o resumo
  // é recalculado: ele depende de tudo isso. Falha aqui não bloqueia a ficha.
  useEffect(() => {
    if (!token || !id || !employee) return;
    getRemunerationSummaryApi(token, id)
      .then(setSummary)
      .catch(() => setSummary(null));
  }, [token, id, employee]);

  useEffect(() => {
    if (!token) return;
    listEmployersApi(token).then(setEmployers).catch(() => setEmployers([]));
    listPositionsApi(token).then(setPositions).catch(() => setPositions([]));
    listCostCentersApi(token).then(setCostCenters).catch(() => setCostCenters([]));
    getStoresPaginatedApi(token, { all: true })
      .then((res) => setStores(res.items ?? []))
      .catch(() => setStores([]));
  }, [token]);

  /** O contrato "corrente" é o único sem rescisão; é nele que a alteração entra. */
  const activeContract = useMemo(
    () => employee?.contracts.find((c) => !c.termination) ?? null,
    [employee]
  );

  const hasAnyContract = (employee?.contracts.length ?? 0) > 0;

  // ── Ações ─────────────────────────────────────────────────────────────────

  const handleDelete = async () => {
    if (!token || !id) return;
    if (!window.confirm("Excluir este cadastro? Use isto apenas para erro de cadastro.")) return;
    try {
      await deleteEmployeeApi(token, id);
      toast.success("Colaborador excluído.");
      navigate(DP_EMPLOYEES_PAGE());
    } catch (error: any) {
      toast.error(error?.message || "Erro ao excluir.");
    }
  };

  const handleSuggestion = async () => {
    if (!token || !id) return;
    try {
      setSuggestion(await getUserSuggestionApi(token, id));
    } catch (error: any) {
      toast.error(error?.message || "Erro ao buscar sugestão.");
    }
  };

  const handleLink = async (userId: string) => {
    if (!token || !id) return;
    try {
      setEmployee(await linkEmployeeUserApi(token, id, userId));
      setSuggestion(null);
      toast.success("Conta vinculada.");
    } catch (error: any) {
      toast.error(error?.message || "Erro ao vincular.");
    }
  };

  const handleUnlink = async () => {
    if (!token || !id) return;
    try {
      setEmployee(await unlinkEmployeeUserApi(token, id));
      toast.success("Vínculo desfeito.");
    } catch (error: any) {
      toast.error(error?.message || "Erro ao desfazer o vínculo.");
    }
  };

  const openDependentModal = () => {
    setDependentRows([emptyDependentRow()]);
    setDependentModal(true);
  };

  /** Todos os dependentes do modal num envio só — o backend grava tudo ou nada. */
  const handleCreateDependents = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !id) return;

    const problem = validateDependentRows(dependentRows);
    if (problem) {
      toast.error(problem);
      return;
    }
    const filled = dependentRows.filter((row) => !isBlankDependentRow(row));
    if (filled.length === 0) {
      toast.error("Informe ao menos um dependente.");
      return;
    }

    setSaving(true);
    try {
      await createDependentsBulkApi(token, id, filled.map(toDependentPayload));
      toast.success(filled.length === 1 ? "Dependente cadastrado." : `${filled.length} dependentes cadastrados.`);
      setDependentModal(false);
      await load();
    } catch (error: any) {
      toast.error(error?.message || "Erro ao cadastrar os dependentes.");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateDependent = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !id || !editingDependent) return;

    const problem = validateDependentRows([editingDependent.row]);
    if (problem || isBlankDependentRow(editingDependent.row)) {
      toast.error("Informe nome e data de nascimento do dependente.");
      return;
    }

    setSaving(true);
    try {
      await updateDependentApi(token, id, editingDependent.id, {
        ...toDependentPayload(editingDependent.row),
        // Vazio LIMPA o CPF (o backend trata "" como remoção) — na edição isso é intencional.
        cpf: editingDependent.row.cpf.trim(),
      });
      toast.success("Dependente atualizado.");
      setEditingDependent(null);
      await load();
    } catch (error: any) {
      toast.error(error?.message || "Erro ao editar dependente.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteDependent = async (dependentId: string) => {
    if (!token || !id) return;
    if (!window.confirm("Excluir este dependente?")) return;
    try {
      await deleteDependentApi(token, id, dependentId);
      toast.success("Dependente excluído.");
      await load();
    } catch (error: any) {
      toast.error(error?.message || "Erro ao excluir dependente.");
    }
  };

  const buildVersionPayload = (form: VersionForm): ContractVersionPayload | null => {
    if (!form.positionId || !form.costCenterId) {
      toast.error("Selecione o cargo e o centro de custo.");
      return null;
    }
    if (!form.effectiveFrom) {
      toast.error("Informe a data de início da vigência.");
      return null;
    }
    const salary = Number(form.salaryAmount);
    if (!Number.isFinite(salary) || salary <= 0) {
      toast.error("Informe um salário válido.");
      return null;
    }

    const allowances = buildAllowancePayload(form);
    if (typeof allowances === "string") {
      toast.error(allowances);
      return null;
    }

    return {
      ...allowances,
      effectiveFrom: form.effectiveFrom,
      changeReason: form.changeReason,
      positionId: form.positionId,
      salaryAmount: salary,
      salaryBasis: form.salaryBasis,
      weeklyHours: Number(form.weeklyHours),
      monthlyHours: Number(form.monthlyHours),
      costCenterId: form.costCenterId,
      ...(form.storeId ? { storeId: form.storeId } : {}),
      ...(form.notes.trim() ? { notes: form.notes.trim() } : {}),
    };
  };

  const handleCreateContract = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !id) return;
    if (!contractForm.employerId || !contractForm.admissionDate) {
      toast.error("Informe o empregador e a data de admissão.");
      return;
    }

    // A versão inicial vigora a partir da própria admissão — o backend recusa
    // qualquer outra data, então o formulário nem pergunta.
    const version = buildVersionPayload({
      ...initialVersionForm,
      effectiveFrom: contractForm.admissionDate,
      changeReason: "ADMISSAO",
    });
    if (!version) return;

    setSaving(true);
    try {
      await createContractApi(token, id, {
        employerId: contractForm.employerId,
        contractType: contractForm.contractType,
        admissionDate: contractForm.admissionDate,
        ...(contractForm.probationFirstEndDate
          ? { probationFirstEndDate: contractForm.probationFirstEndDate }
          : {}),
        ...(contractForm.probationEndDate
          ? { probationEndDate: contractForm.probationEndDate }
          : {}),
        ...(contractForm.fixedTermEndDate
          ? { fixedTermEndDate: contractForm.fixedTermEndDate }
          : {}),
        ...(contractForm.registrationNumber.trim()
          ? { registrationNumber: contractForm.registrationNumber.trim() }
          : {}),
        ...(contractForm.notes.trim() ? { notes: contractForm.notes.trim() } : {}),
        initialVersion: version,
      });
      toast.success("Admissão registrada.");
      setContractModal(false);
      setContractForm(emptyContract);
      setInitialVersionForm(emptyAdmissionVersion);
      await load();
    } catch (error: any) {
      toast.error(error?.message || "Erro ao registrar a admissão.");
    } finally {
      setSaving(false);
    }
  };

  const handleAddVersion = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !versionModal) return;

    const payload = buildVersionPayload(versionForm);
    if (!payload) return;
    if (versionEditing && versionEditReason.trim().length < 3) {
      toast.error("Informe o motivo da correção (mínimo de 3 caracteres).");
      return;
    }

    setSaving(true);
    try {
      if (versionEditing) {
        await updateContractVersionApi(token, versionModal, versionEditing.id, {
          ...payload,
          reason: versionEditReason.trim(),
        });
        toast.success("Versão corrigida.");
      } else {
        await addContractVersionApi(token, versionModal, payload);
        toast.success("Alteração contratual registrada.");
      }
      closeVersionModal();
      await load();
    } catch (error: any) {
      toast.error(error?.message || "Erro ao salvar a versão.");
    } finally {
      setSaving(false);
    }
  };

  const closeVersionModal = () => {
    setVersionModal(null);
    setVersionEditing(null);
    setVersionEditReason("");
    setVersionChanges(null);
    setVersionForm(emptyVersion);
  };

  const handleDeleteVersion = async (reason: string) => {
    if (!token || !versionToDelete) return;
    await deleteContractVersionApi(token, versionToDelete.contractId, versionToDelete.version.id, reason);
    toast.success("Versão excluída.");
    setVersionToDelete(null);
    await load();
  };

  /**
   * Alteração nova: pré-preenche com a versão vigente — só o que muda é digitado.
   * Correção (`editing`, ADR 0030): pré-preenche com a própria versão, vigência, motivo e observação inclusive.
   */
  const openVersionModal = (contractId: string, editing: DpContractVersion | null = null) => {
    const contract = employee?.contracts.find((c) => c.id === contractId);
    const current = editing ?? contract?.currentVersion ?? contract?.versions[0];

    setVersionEditing(editing);
    setVersionEditReason("");
    setVersionChanges(null);
    if (editing && token) {
      listContractVersionChangesApi(token, contractId)
        .then(setVersionChanges)
        .catch(() => setVersionChanges([]));
    }

    setVersionForm({
      ...emptyVersion,
      ...(editing
        ? {
            effectiveFrom: editing.effectiveFrom.slice(0, 10),
            changeReason: editing.changeReason,
            notes: editing.notes ?? "",
          }
        : {}),
      positionId: current?.positionId ?? "",
      salaryAmount: current ? String(Number(current.salaryAmount)) : "",
      salaryBasis: current?.salaryBasis ?? "MENSAL",
      weeklyHours: current ? String(Number(current.weeklyHours)) : "44",
      monthlyHours: current ? String(Number(current.monthlyHours)) : "220",
      costCenterId: current?.costCenterId ?? "",
      storeId: current?.storeId ?? "",
      // null (versão anterior à regra) vira campo vazio = continua "não informado".
      insalubrityPercent: decimalToInput(current?.insalubrityPercent),
      insalubrityBase: current?.insalubrityBase ?? "",
      insalubrityBaseAmount: decimalToInput(current?.insalubrityBaseAmount),
      hazardPercent: decimalToInput(current?.hazardPercent),
      hazardBase: current?.hazardBase ?? "",
      hazardBaseAmount: decimalToInput(current?.hazardBaseAmount),
      complementaryActivityPercent: decimalToInput(current?.complementaryActivityPercent),
    });
    setVersionModal(contractId);
  };

  // ── Opções de benefício ──────────────────────────────────────────────────

  /**
   * Registro novo: pré-preenche com o vigente; o que muda é só o que for alterado.
   * Correção (`editing`): pré-preenche com o próprio registro, vigência e observação inclusive.
   */
  const openBenefitModal = (contractId: string, editing: DpBenefitElection | null = null) => {
    const contract = employee?.contracts.find((c) => c.id === contractId);
    const current = editing ?? contract?.currentBenefitElection ?? contract?.benefitElections?.[0] ?? null;

    setBenefitForm(
      current
        ? {
            ...emptyBenefit,
            ...(editing ? { effectiveFrom: editing.effectiveFrom.slice(0, 10), notes: editing.notes ?? "" } : {}),
            salaryAdvanceOptIn: current.salaryAdvanceOptIn,
            salaryAdvancePercent: decimalToInput(current.salaryAdvancePercent) || emptyBenefit.salaryAdvancePercent,
            transportVoucherOptIn: current.transportVoucherOptIn,
            transportVoucherDailyAmount: decimalToInput(current.transportVoucherDailyAmount),
            transportVoucherEmployeePct: current.transportVoucherOptIn
              ? decimalToInput(current.transportVoucherEmployeePct)
              : emptyBenefit.transportVoucherEmployeePct,
            mealVoucherOptIn: current.mealVoucherOptIn,
            mealVoucherDailyAmount: decimalToInput(current.mealVoucherDailyAmount),
            mealVoucherEmployeePct: decimalToInput(current.mealVoucherEmployeePct),
            foodVoucherOptIn: current.foodVoucherOptIn === true,
            foodVoucherAmount: decimalToInput(current.foodVoucherAmount),
            foodVoucherMode: current.foodVoucherMode ?? "POR_DIA",
            foodVoucherEmployeePct: decimalToInput(current.foodVoucherEmployeePct),
            motorcycleRentalOptIn: current.motorcycleRentalOptIn === true,
            motorcycleRentalAmount: decimalToInput(current.motorcycleRentalAmount),
            fuelVoucherOptIn: current.fuelVoucherOptIn === true,
            fuelVoucherAmount: decimalToInput(current.fuelVoucherAmount),
            fuelVoucherMode: current.fuelVoucherMode ?? "POR_DIA",
          }
        : emptyBenefit
    );
    setBenefitEditing(editing);
    setBenefitModal(contractId);
  };

  const handleDeleteBenefit = async () => {
    if (!token || !benefitToDelete) return;
    setSaving(true);
    try {
      await deleteBenefitElectionApi(token, benefitToDelete.contractId, benefitToDelete.election.id);
      toast.success("Opção de benefício excluída.");
      setBenefitToDelete(null);
      await load();
    } catch (error: any) {
      toast.error(error?.message || "Erro ao excluir as opções de benefício.");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveBenefit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !benefitModal) return;

    const f = benefitForm;
    if (!f.effectiveFrom) {
      toast.error("Informe a data a partir da qual as opções valem.");
      return;
    }

    const num = (value: string) => (value.trim() === "" ? undefined : Number(value));
    const payload: BenefitElectionPayload = {
      effectiveFrom: f.effectiveFrom,
      salaryAdvanceOptIn: f.salaryAdvanceOptIn,
      transportVoucherOptIn: f.transportVoucherOptIn,
      mealVoucherOptIn: f.mealVoucherOptIn,
      foodVoucherOptIn: f.foodVoucherOptIn,
      motorcycleRentalOptIn: f.motorcycleRentalOptIn,
      fuelVoucherOptIn: f.fuelVoucherOptIn,
      // Valores só de quem optou: o backend recusa valor em benefício não optado.
      ...(f.salaryAdvanceOptIn ? { salaryAdvancePercent: num(f.salaryAdvancePercent) } : {}),
      ...(f.transportVoucherOptIn
        ? {
            transportVoucherDailyAmount: num(f.transportVoucherDailyAmount),
            transportVoucherEmployeePct: num(f.transportVoucherEmployeePct),
          }
        : {}),
      ...(f.mealVoucherOptIn
        ? {
            mealVoucherDailyAmount: num(f.mealVoucherDailyAmount),
            mealVoucherEmployeePct: num(f.mealVoucherEmployeePct),
          }
        : {}),
      ...(f.foodVoucherOptIn
        ? {
            foodVoucherAmount: num(f.foodVoucherAmount),
            foodVoucherMode: f.foodVoucherMode,
            foodVoucherEmployeePct: num(f.foodVoucherEmployeePct),
          }
        : {}),
      ...(f.motorcycleRentalOptIn ? { motorcycleRentalAmount: num(f.motorcycleRentalAmount) } : {}),
      ...(f.fuelVoucherOptIn
        ? { fuelVoucherAmount: num(f.fuelVoucherAmount), fuelVoucherMode: f.fuelVoucherMode }
        : {}),
      ...(f.notes.trim() ? { notes: f.notes.trim() } : {}),
    };

    setSaving(true);
    try {
      if (benefitEditing) {
        await updateBenefitElectionApi(token, benefitModal, benefitEditing.id, payload);
        toast.success("Opções de benefício corrigidas.");
      } else {
        await createBenefitElectionApi(token, benefitModal, payload);
        toast.success("Opções de benefício registradas.");
      }
      setBenefitModal(null);
      await load();
    } catch (error: any) {
      toast.error(error?.message || "Erro ao registrar as opções de benefício.");
    } finally {
      setSaving(false);
    }
  };

  // ── Blocos de formulário reutilizados pelos dois modais de versão ──────────

  /** Percentual · base · valor da base de um adicional. */
  const allowanceFields = (
    form: VersionForm,
    setForm: React.Dispatch<React.SetStateAction<VersionForm>>,
    prefix: AllowancePrefix
  ) => {
    const percentKey = `${prefix}Percent` as const;
    const baseKey = `${prefix}Base` as const;
    const amountKey = `${prefix}BaseAmount` as const;
    const percent = form[percentKey].trim();
    const needsBase = percent !== "" && Number(percent) > 0;

    return (
      <div className="dpFormGrid">
        <div className="dpFormGroup">
          <label htmlFor={percentKey}>{ALLOWANCE_LABELS[prefix]} (%)</label>
          <input
            id={percentKey}
            type="number"
            step="0.01"
            min="0"
            max="100"
            className="dpInput"
            placeholder="não informado"
            value={form[percentKey]}
            onChange={(e) => {
              const value = e.target.value;
              setForm((f) => ({
                ...f,
                [percentKey]: value,
                // Sem percentual (ou 0%), não há base — o backend recusaria.
                ...(value.trim() === "" || Number(value) === 0
                  ? { [baseKey]: "", [amountKey]: "" }
                  : {}),
              }));
            }}
            disabled={saving}
          />
        </div>

        <div className="dpFormGroup">
          <label htmlFor={baseKey}>Base de cálculo{needsBase ? " *" : ""}</label>
          <select
            id={baseKey}
            className="dpSelect"
            value={form[baseKey]}
            onChange={(e) =>
              setForm((f) => ({
                ...f,
                [baseKey]: e.target.value as DpAllowanceBase | "",
                ...(e.target.value !== "VALOR_INFORMADO" ? { [amountKey]: "" } : {}),
              }))
            }
            disabled={saving || !needsBase}
          >
            <option value="">{needsBase ? "Selecione..." : "—"}</option>
            {(Object.keys(ALLOWANCE_BASE_LABELS) as DpAllowanceBase[]).map((b) => (
              <option key={b} value={b}>
                {ALLOWANCE_BASE_LABELS[b]}
              </option>
            ))}
          </select>
        </div>

        {form[baseKey] === "VALOR_INFORMADO" ? (
          <div className="dpFormGroup">
            <label htmlFor={amountKey}>Valor da base (R$) *</label>
            <input
              id={amountKey}
              type="number"
              step="0.01"
              min="0"
              className="dpInput"
              value={form[amountKey]}
              onChange={(e) => setForm((f) => ({ ...f, [amountKey]: e.target.value }))}
              disabled={saving}
            />
          </div>
        ) : null}
      </div>
    );
  };

  const versionFields = (
    form: VersionForm,
    setForm: React.Dispatch<React.SetStateAction<VersionForm>>,
    options: {
      showReason: boolean;
      showEffectiveFrom: boolean;
      /** Correção da versão de admissão: vigência e motivo ficam como estão. */
      lockAdmission?: boolean;
      /** Correção: a vigência fica entre as versões vizinhas, não depois da última. */
      editing?: boolean;
    }
  ) => {
    // A loja de lotação só pode ser uma das lojas agrupadas pelo centro de custo escolhido.
    const centerStoreIds = costCenters.find((cc) => cc.id === form.costCenterId)?.storeIds ?? [];
    const centerStores = stores.filter((s) => centerStoreIds.includes(s.id));
    // Versão vigente com loja fora do centro (dado anterior à regra): mostrada para o usuário corrigir.
    const storeOutsideCenter =
      form.storeId && !centerStoreIds.includes(form.storeId)
        ? stores.find((s) => s.id === form.storeId) ?? { id: form.storeId, nomeFantasia: "Loja atual" }
        : null;

    return (
    <>
      {options.showEffectiveFrom ? (
        <div className="dpFormGroup">
          <label htmlFor="effectiveFrom">Vigente a partir de *</label>
          <input
            id="effectiveFrom"
            type="date"
            className="dpInput"
            value={form.effectiveFrom}
            onChange={(e) => setForm((f) => ({ ...f, effectiveFrom: e.target.value }))}
            disabled={saving || options.lockAdmission}
          />
          <span className="dpFormHint">
            {options.lockAdmission
              ? "Versão de admissão: a vigência é a data de admissão."
              : options.editing
                ? "Precisa ficar entre a vigência da versão anterior e a da seguinte."
                : "Precisa ser posterior à última vigência — o histórico não aceita retroagir."}
          </span>
        </div>
      ) : null}

      {options.showReason ? (
        <div className="dpFormGroup">
          <label htmlFor="changeReason">Motivo da alteração *</label>
          <select
            id="changeReason"
            className="dpSelect"
            value={form.changeReason}
            onChange={(e) =>
              setForm((f) => ({ ...f, changeReason: e.target.value as DpContractChangeReason }))
            }
            disabled={saving || options.lockAdmission}
          >
            {(options.lockAdmission ? [form.changeReason] : CONTRACT_CHANGE_REASONS_SELECTABLE).map((r) => (
              <option key={r} value={r}>
                {CONTRACT_CHANGE_REASON_LABELS[r]}
              </option>
            ))}
          </select>
        </div>
      ) : null}

      <div className="dpFormGroup">
        <label htmlFor="positionId">Cargo *</label>
        <select
          id="positionId"
          className="dpSelect"
          value={form.positionId}
          onChange={(e) => setForm((f) => ({ ...f, positionId: e.target.value }))}
          disabled={saving}
        >
          <option value="">Selecione...</option>
          {positions.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
              {p.cbo ? ` (CBO ${p.cbo})` : ""}
            </option>
          ))}
        </select>
      </div>

      <div className="dpFormGroup">
        <label htmlFor="costCenterId">Centro de custo *</label>
        <select
          id="costCenterId"
          className="dpSelect"
          value={form.costCenterId}
          onChange={(e) => {
            const costCenterId = e.target.value;
            const ids = costCenters.find((cc) => cc.id === costCenterId)?.storeIds ?? [];
            const available = stores.filter((s) => ids.includes(s.id)).map((s) => s.id);
            setForm((f) => ({
              ...f,
              costCenterId,
              // Mantém a loja se ela pertence ao novo centro; se o centro tem uma loja só, já a escolhe.
              storeId: available.includes(f.storeId)
                ? f.storeId
                : available.length === 1
                  ? available[0]
                  : "",
            }));
          }}
          disabled={saving}
        >
          <option value="">Selecione...</option>
          {costCenters.map((cc) => (
            <option key={cc.id} value={cc.id}>
              {cc.code} — {cc.name}
            </option>
          ))}
        </select>
      </div>

      <div className="dpFormGroup">
        <label htmlFor="storeId">Loja de lotação</label>
        <select
          id="storeId"
          className="dpSelect"
          value={form.storeId}
          onChange={(e) => setForm((f) => ({ ...f, storeId: e.target.value }))}
          disabled={saving || !form.costCenterId}
        >
          <option value="">— sem loja —</option>
          {centerStores.map((s) => (
            <option key={s.id} value={s.id}>
              {s.nomeFantasia}
            </option>
          ))}
          {storeOutsideCenter ? (
            <option value={storeOutsideCenter.id}>
              {storeOutsideCenter.nomeFantasia} (fora deste centro de custo)
            </option>
          ) : null}
        </select>
        {!form.costCenterId ? (
          <span className="dpFormHint">Escolha o centro de custo para ver as lojas dele.</span>
        ) : storeOutsideCenter ? (
          <span className="dpFormHint dpFormHint--error">
            A loja atual não pertence a este centro de custo. Escolha outra loja ou vincule-a ao
            centro de custo antes de salvar.
          </span>
        ) : centerStores.length === 0 ? (
          <span className="dpFormHint">
            Este centro de custo não agrupa nenhuma loja. Vincule as lojas em Cadastros, Centros de
            custo.
          </span>
        ) : null}
      </div>

      <div className="dpFormGroup">
        <label htmlFor="salaryAmount">Salário *</label>
        <input
          id="salaryAmount"
          type="number"
          step="0.01"
          min="0"
          className="dpInput"
          value={form.salaryAmount}
          onChange={(e) => setForm((f) => ({ ...f, salaryAmount: e.target.value }))}
          disabled={saving}
        />
      </div>

      <div className="dpFormGroup">
        <label htmlFor="salaryBasis">Base do salário *</label>
        <select
          id="salaryBasis"
          className="dpSelect"
          value={form.salaryBasis}
          onChange={(e) =>
            setForm((f) => ({ ...f, salaryBasis: e.target.value as DpSalaryBasis }))
          }
          disabled={saving}
        >
          {(Object.keys(SALARY_BASIS_LABELS) as DpSalaryBasis[]).map((b) => (
            <option key={b} value={b}>
              {SALARY_BASIS_LABELS[b]}
            </option>
          ))}
        </select>
      </div>

      <div className="dpFormGroup">
        <label htmlFor="weeklyHours">Jornada semanal (horas) *</label>
        <input
          id="weeklyHours"
          type="number"
          step="0.5"
          min="1"
          max="44"
          className="dpInput"
          value={form.weeklyHours}
          onChange={(e) => setForm((f) => ({ ...f, weeklyHours: e.target.value }))}
          disabled={saving}
        />
      </div>

      <div className="dpFormGroup">
        <label htmlFor="monthlyHours">Divisor mensal (horas) *</label>
        <input
          id="monthlyHours"
          type="number"
          step="0.5"
          min="1"
          className="dpInput"
          value={form.monthlyHours}
          onChange={(e) => setForm((f) => ({ ...f, monthlyHours: e.target.value }))}
          disabled={saving}
        />
        <span className="dpFormHint">
          Base do salário-hora. Não é derivado da jornada semanal: 12x36 e 6x1 dariam valor errado.
        </span>
      </div>

      <hr style={{ border: 0, borderTop: "1px solid #e2e8f0", width: "100%" }} />
      <h3 className="dpSection__title" style={{ marginBottom: 0 }}>
        Adicionais
      </h3>
      <span className="dpFormHint" style={{ marginTop: -8 }}>
        Condição do contrato: percentual e base. O valor do mês (proporcional) é apurado no
        fechamento. Deixe o percentual vazio para "não informado"; 0 = não recebe.
      </span>
      {allowanceFields(form, setForm, "insalubrity")}
      {allowanceFields(form, setForm, "hazard")}
      <div className="dpFormGrid">
        <div className="dpFormGroup">
          <label htmlFor="complementaryActivityPercent">Atividade complementar (%)</label>
          <input
            id="complementaryActivityPercent"
            type="number"
            step="0.01"
            min="0"
            max="100"
            className="dpInput"
            placeholder="não informado"
            value={form.complementaryActivityPercent}
            onChange={(e) => setForm((f) => ({ ...f, complementaryActivityPercent: e.target.value }))}
            disabled={saving}
          />
        </div>
        <div className="dpFormGroup">
          <label htmlFor="complementaryActivityBase">Base de cálculo</label>
          {/* Base fixa: sempre o salário contratual — só exibida. */}
          <input
            id="complementaryActivityBase"
            className="dpInput"
            value={
              form.complementaryActivityPercent.trim() !== "" && Number(form.complementaryActivityPercent) > 0
                ? ALLOWANCE_BASE_LABELS.SALARIO_CONTRATUAL
                : "—"
            }
            disabled
            readOnly
          />
        </div>
      </div>

      <div className="dpFormGroup">
        <label htmlFor="versionNotes">Observações</label>
        <textarea
          id="versionNotes"
          className="dpTextarea"
          value={form.notes}
          maxLength={1000}
          onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
          disabled={saving}
        />
      </div>
    </>
    );
  };

  if (!token) return null;

  return (
    <div className="dpLayout">
      <main className="dpMain">
        <div className="dpContainer">
          {loading || !employee ? (
            <div className="dpEmpty">Carregando...</div>
          ) : (
            <>
              <EcoPageHeader
                crumbs={[
                  { label: "Departamento Pessoal", onClick: () => navigate(DP_PAGE()) },
                  { label: "Colaboradores", onClick: () => navigate(DP_EMPLOYEES_PAGE()) },
                  { label: employee.socialName || employee.fullName },
                ]}
                leading={
                  <EmployeePhoto
                    token={token}
                    employeeId={employee.id}
                    fullName={employee.fullName}
                    photoAssetId={employee.photoAssetId}
                    onChanged={load}
                  />
                }
                title={employee.fullName}
                subtitle={
                  <>
                    CPF {formatCpf(employee.cpf)} · Nascimento {formatDateOnly(employee.birthDate)}
                    {employee.activeLeave ? (
                      <>
                        <br />
                        Afastado: {LEAVE_TYPE_LABELS[employee.activeLeave.type]} desde{" "}
                        {formatDateOnly(employee.activeLeave.startDate)}
                      </>
                    ) : employee.nextVacation ? (
                      <>
                        <br />
                        Férias: {formatDateOnly(employee.nextVacation.startDate)} a{" "}
                        {formatDateOnly(employee.nextVacation.endDate)}
                      </>
                    ) : null}
                  </>
                }
                onBack={goBack}
                backLabel="Voltar"
                actions={
                  <>
                    <span className="dpeHeaderBadge">
                      <EmployeeStatusBadge status={employee.status} />
                    </span>
                    {!hasAnyContract ? (
                      <button type="button" className="ecoBtn ecoBtn--danger" onClick={handleDelete}>
                        Excluir
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="ecoBtn ecoBtn--primary"
                      onClick={() => navigate(DP_EMPLOYEE_EDIT_PAGE(employee.id))}
                    >
                      Editar
                    </button>
                  </>
                }
              />

              <nav className="dpTabs">
                <button
                  className={`dpTab ${tab === "dados" ? "is-active" : ""}`}
                  onClick={() => setTab("dados")}
                >
                  Dados
                </button>
                <button
                  className={`dpTab ${tab === "dependentes" ? "is-active" : ""}`}
                  onClick={() => setTab("dependentes")}
                >
                  Dependentes ({employee.dependents.length})
                </button>
                <button
                  className={`dpTab ${tab === "contrato" ? "is-active" : ""}`}
                  onClick={() => setTab("contrato")}
                >
                  Contrato ({employee.contracts.length})
                </button>
                <button
                  className={`dpTab ${tab === "escala" ? "is-active" : ""}`}
                  onClick={() => setTab("escala")}
                >
                  Escala
                </button>
                <button
                  className={`dpTab ${tab === "ponto" ? "is-active" : ""}`}
                  onClick={() => setTab("ponto")}
                >
                  Ponto
                </button>
                <button
                  className={`dpTab ${tab === "documentos" ? "is-active" : ""}`}
                  onClick={() => setTab("documentos")}
                >
                  Documentos
                </button>
                <button
                  className={`dpTab ${tab === "processos" ? "is-active" : ""}`}
                  onClick={() => setTab("processos")}
                >
                  Admissão e desligamento
                </button>
                <button
                  className={`dpTab ${tab === "ferias" ? "is-active" : ""}`}
                  onClick={() => setTab("ferias")}
                >
                  Férias
                </button>
                <button
                  className={`dpTab ${tab === "provisoes" ? "is-active" : ""}`}
                  onClick={() => setTab("provisoes")}
                >
                  Provisões
                </button>
                <button
                  className={`dpTab ${tab === "afastamentos" ? "is-active" : ""}`}
                  onClick={() => setTab("afastamentos")}
                >
                  Afastamentos
                </button>
                <button
                  className={`dpTab ${tab === "acesso" ? "is-active" : ""}`}
                  onClick={() => setTab("acesso")}
                >
                  Acesso ao app
                </button>
              </nav>

              {/* ── Aba: Dados ───────────────────────────────────────────── */}
              {tab === "dados" ? (
                <>
                  <section className="dpSection">
                    <h2 className="dpSection__title">Identificação</h2>
                    <div className="dpDefinitionGrid">
                      <Field label="Nome social" value={orDash(employee.socialName)} />
                      <Field label="Gênero" value={GENDER_LABELS[employee.gender]} />
                      <Field
                        label="Estado civil"
                        value={
                          employee.maritalStatus
                            ? MARITAL_STATUS_LABELS[employee.maritalStatus]
                            : "—"
                        }
                      />
                      <Field label="Nacionalidade" value={orDash(employee.nationality)} />
                      <Field
                        label="Naturalidade"
                        value={
                          employee.birthCity
                            ? `${employee.birthCity}${employee.birthState ? `/${employee.birthState}` : ""}`
                            : "—"
                        }
                      />
                      <Field label="Nome da mãe" value={orDash(employee.motherName)} />
                      <Field label="Nome do pai" value={orDash(employee.fatherName)} />
                      <Field label="E-mail" value={orDash(employee.email)} />
                      <Field label="Telefone" value={orDash(employee.phone)} />
                    </div>
                  </section>

                  <section className="dpSection">
                    <h2 className="dpSection__title">Documentos</h2>
                    <div className="dpDefinitionGrid">
                      <Field
                        label="RG"
                        value={
                          employee.rg
                            ? `${employee.rg}${employee.rgIssuer ? ` — ${employee.rgIssuer}` : ""}`
                            : "—"
                        }
                      />
                      <Field label="Emissão do RG" value={formatDateOnly(employee.rgIssuedAt)} />
                      <Field
                        label="CTPS"
                        value={
                          employee.ctpsNumber
                            ? `${employee.ctpsNumber}${employee.ctpsSeries ? ` / ${employee.ctpsSeries}` : ""}${employee.ctpsState ? ` — ${employee.ctpsState}` : ""}`
                            : "—"
                        }
                      />
                      <Field label="PIS/NIS" value={orDash(employee.pisNis)} />
                      <Field label="Título de eleitor" value={orDash(employee.voterTitle)} />
                      <Field label="Reservista" value={orDash(employee.militaryCert)} />
                      <Field
                        label="CNH"
                        value={
                          employee.cnh
                            ? `${employee.cnh}${employee.cnhCategory ? ` (${employee.cnhCategory})` : ""}`
                            : "—"
                        }
                      />
                      <Field label="Validade da CNH" value={formatDateOnly(employee.cnhExpiresAt)} />
                    </div>
                  </section>

                  <section className="dpSection">
                    <h2 className="dpSection__title">Endereço</h2>
                    <div className="dpDefinitionGrid">
                      <Field
                        label="Logradouro"
                        value={
                          employee.addressStreet
                            ? `${employee.addressStreet}${employee.addressNumber ? `, ${employee.addressNumber}` : ""}${employee.addressComplement ? ` — ${employee.addressComplement}` : ""}`
                            : "—"
                        }
                      />
                      <Field label="Bairro" value={orDash(employee.addressDistrict)} />
                      <Field
                        label="Cidade/UF"
                        value={
                          employee.addressCity
                            ? `${employee.addressCity}${employee.addressState ? `/${employee.addressState}` : ""}`
                            : "—"
                        }
                      />
                      <Field label="CEP" value={orDash(employee.addressZip)} />
                    </div>
                  </section>

                  <section className="dpSection">
                    <h2 className="dpSection__title">Dados bancários</h2>
                    <div className="dpDefinitionGrid">
                      <Field
                        label="Banco"
                        value={
                          employee.bankName || employee.bankCode
                            ? `${employee.bankCode ?? ""} ${employee.bankName ?? ""}`.trim()
                            : "—"
                        }
                      />
                      <Field label="Agência" value={orDash(employee.bankBranch)} />
                      <Field label="Conta" value={orDash(employee.bankAccount)} />
                      <Field
                        label="Tipo de conta"
                        value={
                          employee.bankAccountType
                            ? BANK_ACCOUNT_TYPE_LABELS[employee.bankAccountType]
                            : "—"
                        }
                      />
                      <Field label="Chave PIX" value={orDash(employee.pixKey)} />
                    </div>
                  </section>

                  <section className="dpSection">
                    <h2 className="dpSection__title">Contato de emergência</h2>
                    <div className="dpDefinitionGrid">
                      <Field label="Nome" value={orDash(employee.emergencyContactName)} />
                      <Field label="Telefone" value={orDash(employee.emergencyContactPhone)} />
                      <Field
                        label="Parentesco"
                        value={orDash(employee.emergencyContactRelationship)}
                      />
                    </div>
                  </section>

                  <section className="dpSection">
                    <h2 className="dpSection__title">Conta de acesso ao sistema</h2>
                    <p className="dpNotice">
                      Informativo. Serve para relatórios do tipo "quais supervisores são CLT" e
                      não dá acesso a nada: o colaborador não usa esta conta.
                    </p>

                    {employee.user ? (
                      <div className="dpDefinitionGrid">
                        <Field label="Usuário" value={employee.user.nomeCompleto} />
                        <Field label="Perfil" value={employee.user.userType} />
                        <div>
                          <button className="dpBtn dpBtn--secondary" onClick={handleUnlink}>
                            Desfazer vínculo
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <button className="dpBtn dpBtn--secondary" onClick={handleSuggestion}>
                          Procurar conta com o mesmo CPF
                        </button>
                        {suggestion ? (
                          suggestion.user ? (
                            <div style={{ marginTop: 16 }} className="dpDefinitionGrid">
                              <Field label="Encontrado" value={suggestion.user.nomeCompleto} />
                              <Field label="Perfil" value={suggestion.user.userType} />
                              <div>
                                <button
                                  className="dpBtn dpBtn--primary"
                                  onClick={() => handleLink(suggestion.user!.id)}
                                >
                                  Vincular
                                </button>
                              </div>
                            </div>
                          ) : (
                            <p className="dpFormHint" style={{ marginTop: 12 }}>
                              Nenhuma conta de sistema com este CPF.
                            </p>
                          )
                        ) : null}
                      </>
                    )}
                  </section>

                  {employee.notes ? (
                    <section className="dpSection">
                      <h2 className="dpSection__title">Observações</h2>
                      <p className="dpDefinition__value">{employee.notes}</p>
                    </section>
                  ) : null}
                </>
              ) : null}

              {/* ── Aba: Dependentes ─────────────────────────────────────── */}
              {tab === "dependentes" ? (
                <section className="dpSection">
                  <div className="dpHeader" style={{ marginBottom: 16 }}>
                    <h2 className="dpSection__title" style={{ margin: 0 }}>
                      Dependentes
                    </h2>
                    <button className="dpBtn dpBtn--primary" onClick={openDependentModal}>
                      Novo dependente
                    </button>
                  </div>

                  <div className="dpCounters">
                    <div className="dpCounter">
                      <div className="dpCounter__value">{summary ? summary.salarioFamilia.nf : "—"}</div>
                      <div className="dpCounter__label">
                        NF · filhos com direito a salário-família
                        {summary?.salarioFamilia.quotaTotal && summary.salarioFamilia.nf > 0
                          ? ` (${formatCurrency(summary.salarioFamilia.quotaTotal)}/mês)`
                          : ""}
                      </div>
                    </div>
                    <div className="dpCounter">
                      <div className="dpCounter__value">{summary ? summary.nd : "—"}</div>
                      <div className="dpCounter__label">ND · dependentes de IRRF</div>
                    </div>
                  </div>

                  {summary?.warnings.map((warning) => (
                    <p key={warning} className="dpNotice dpNotice--warning">
                      {warning}
                    </p>
                  ))}

                  <p className="dpNotice">
                    O sistema confere o salário-família pela idade do filho (menos de 14 anos, ou
                    com deficiência), pela remuneração (salário + adicionais) e pelo teto vigente em
                    Cadastros → Parâmetros legais. A folha só paga o filho com a marcação de
                    salário-família (certidão e vacinação/frequência entregues) e com direito pelo cálculo —
                    quando a marcação diverge do cálculo, a tabela avisa. IRRF é regra à parte.
                  </p>

                  <div className="dpTableWrapper">
                    <table className="dpTable">
                      <thead>
                        <tr>
                          <th>Nome</th>
                          <th>Parentesco</th>
                          <th>Nascimento</th>
                          <th>CPF</th>
                          <th>IRRF</th>
                          <th>Sal. família (marcado)</th>
                          <th>Direito calculado</th>
                          <th style={{ width: 170 }}>Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {employee.dependents.length === 0 ? (
                          <tr>
                            <td colSpan={8} className="dpEmpty">
                              Nenhum dependente cadastrado.
                            </td>
                          </tr>
                        ) : (
                          employee.dependents.map((d) => (
                            <tr key={d.id}>
                              <td>{d.fullName}</td>
                              <td>{DEPENDENT_RELATIONSHIP_LABELS[d.relationship]}</td>
                              <td>{formatDateOnly(d.birthDate)}</td>
                              <td>{d.cpf ? formatCpf(d.cpf) : "—"}</td>
                              <td>{d.isIrDependent ? "Sim" : "Não"}</td>
                              <td>{d.isSalarioFamiliaEligible ? "Sim" : "Não"}</td>
                              <td>
                                <SalarioFamiliaCell
                                  evaluation={summary?.salarioFamilia.dependents.find(
                                    (x) => x.dependentId === d.id
                                  )}
                                />
                              </td>
                              <td>
                                <div className="dpTable__actions">
                                  <button
                                    className="dpBtn dpBtn--secondary dpBtn--small"
                                    onClick={() =>
                                      setEditingDependent({ id: d.id, row: dependentToRow(d) })
                                    }
                                  >
                                    Editar
                                  </button>
                                  <button
                                    className="dpBtn dpBtn--danger dpBtn--small"
                                    onClick={() => handleDeleteDependent(d.id)}
                                  >
                                    Excluir
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </section>
              ) : null}

              {/* ── Aba: Contrato ────────────────────────────────────────── */}
              {tab === "contrato" ? (
                <section className="dpSection">
                  <div className="dpHeader" style={{ marginBottom: 16 }}>
                    <h2 className="dpSection__title" style={{ margin: 0 }}>
                      Contrato de trabalho
                    </h2>
                    {!activeContract ? (
                      <button
                        className="dpBtn dpBtn--primary"
                        onClick={() => setContractModal(true)}
                      >
                        Registrar admissão
                      </button>
                    ) : null}
                  </div>

                  <p className="dpNotice">
                    Reajuste, promoção, transferência e mudança de jornada são registrados como
                    versão nova, com a data em que passam a valer. Versão já registrada nunca é
                    alterada — é assim que a ficha responde "qual era o salário em março de 2024?".
                  </p>

                  {employee.contracts.length === 0 ? (
                    <div className="dpEmpty">Nenhum contrato registrado.</div>
                  ) : (
                    employee.contracts.map((contract) => (
                      <div key={contract.id} style={{ marginBottom: 32 }}>
                        <div className="dpDefinitionGrid" style={{ marginBottom: 16 }}>
                          <Field label="Empregador" value={contract.employer.legalName} />
                          <Field label="CNPJ" value={formatCnpj(contract.employer.cnpj)} />
                          <Field
                            label="Tipo"
                            value={CONTRACT_TYPE_LABELS[contract.contractType]}
                          />
                          <Field
                            label="Admissão"
                            value={formatDateOnly(contract.admissionDate)}
                          />
                          <Field
                            label="Fim do 1º período"
                            value={formatDateOnly(contract.probationFirstEndDate)}
                          />
                          <Field
                            label="Fim da experiência"
                            value={
                              contract.probationEndDate ? (
                                <>
                                  {formatDateOnly(contract.probationEndDate)}{" "}
                                  {!contract.termination &&
                                  probationStateOf(contract) !== "ENCERRADA" ? (
                                    <span className="dpBadge dpBadge--blue">
                                      {PROBATION_STATE_LABELS[probationStateOf(contract)]}
                                    </span>
                                  ) : null}
                                </>
                              ) : (
                                "—"
                              )
                            }
                          />
                          <Field
                            label="Término do contrato"
                            value={formatDateOnly(contract.fixedTermEndDate)}
                          />
                          <Field
                            label="Matrícula"
                            value={orDash(contract.registrationNumber)}
                          />
                          <Field
                            label="Situação"
                            value={
                              contract.termination ? (
                                <>
                                  Rescindido em {formatDateOnly(contract.termination.terminationDate)}{" "}
                                  <button
                                    type="button"
                                    className="dpBtn dpBtn--secondary dpBtn--small"
                                    onClick={() => setTerminationContractId(contract.id)}
                                    title="Demonstrativo de rescisão (conferência)"
                                  >
                                    Rescisão
                                  </button>
                                </>
                              ) : (
                                "Vigente"
                              )
                            }
                          />
                        </div>

                        {!contract.termination && summary?.contract ? (
                          <RemunerationCard summary={summary} />
                        ) : null}

                        <BenefitsCard
                          elections={contract.benefitElections ?? []}
                          onEdit={contract.termination ? null : () => openBenefitModal(contract.id)}
                          onEditRow={(election) => openBenefitModal(contract.id, election)}
                          onDeleteRow={(election) => setBenefitToDelete({ contractId: contract.id, election })}
                        />

                        {token ? (
                          <ContractBenefitsCard
                            token={token}
                            contractId={contract.id}
                            readOnly={!!contract.termination}
                          />
                        ) : null}

                        {token ? (
                          <RecurringDeductionsCard
                            token={token}
                            contractId={contract.id}
                            readOnly={!!contract.termination}
                          />
                        ) : null}

                        {token ? (
                          <VariableHistoryCard token={token} contractId={contract.id} readOnly={!!contract.termination} />
                        ) : null}

                        {!contract.termination ? (
                          <button
                            className="dpBtn dpBtn--primary"
                            style={{ marginBottom: 16 }}
                            onClick={() => openVersionModal(contract.id)}
                          >
                            Nova alteração contratual
                          </button>
                        ) : null}
                        {!contract.termination ? (
                          <button
                            className="dpBtn dpBtn--secondary"
                            style={{ marginBottom: 16, marginLeft: 8 }}
                            onClick={() => setProbationContract(contract)}
                          >
                            Editar experiência
                          </button>
                        ) : null}

                        <div className="dpTimeline">
                          {contract.versions.map((v) => (
                            <article
                              key={v.id}
                              className={`dpTimelineItem ${v.effectiveTo === null ? "is-current" : ""}`}
                            >
                              <div className="dpTimelineItem__header">
                                <span className="dpTimelineItem__period">
                                  {formatDateOnly(v.effectiveFrom)} —{" "}
                                  {v.effectiveTo ? formatDateOnly(v.effectiveTo) : "vigente"}
                                </span>
                                <span style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
                                  <span className="dpBadge dpBadge--blue">
                                    {CONTRACT_CHANGE_REASON_LABELS[v.changeReason]}
                                  </span>
                                  {!contract.termination ? (
                                    <>
                                      <button
                                        type="button"
                                        className="dpBtn dpBtn--secondary dpBtn--small"
                                        onClick={() => openVersionModal(contract.id, v)}
                                      >
                                        Editar
                                      </button>
                                      {v.changeReason !== "ADMISSAO" ? (
                                        <button
                                          type="button"
                                          className="dpBtn dpBtn--danger dpBtn--small"
                                          onClick={() => setVersionToDelete({ contractId: contract.id, version: v })}
                                        >
                                          Excluir
                                        </button>
                                      ) : null}
                                    </>
                                  ) : null}
                                </span>
                              </div>
                              <div className="dpDefinitionGrid">
                                <Field
                                  label="Cargo"
                                  value={`${v.position.name}${v.cbo ? ` (CBO ${v.cbo})` : ""}`}
                                />
                                <Field
                                  label="Salário"
                                  value={`${formatCurrency(v.salaryAmount)} (${SALARY_BASIS_LABELS[v.salaryBasis]})`}
                                />
                                <Field
                                  label="Jornada"
                                  value={`${formatHours(v.weeklyHours)}/semana · divisor ${formatHours(v.monthlyHours)}`}
                                />
                                <Field
                                  label="Centro de custo"
                                  value={`${v.costCenter.code} — ${v.costCenter.name}`}
                                />
                                <Field
                                  label="Loja"
                                  value={orDash(v.store?.nomeFantasia)}
                                />
                                <Field label="Adicionais" value={<AllowancesSummary version={v} />} />
                                {v.notes ? <Field label="Observações" value={v.notes} /> : null}
                              </div>
                            </article>
                          ))}
                        </div>
                      </div>
                    ))
                  )}
                </section>
              ) : null}

              {/* ── Aba: Acesso ao app ───────────────────────────────────── */}
              {tab === "documentos" ? (
                <EmployeeDocumentsTab token={token} employee={employee} highlightedId={highlightedDocumentId} />
              ) : null}

              {tab === "processos" ? (
                <EmployeeProcessesTab token={token} employee={employee} onChanged={load} />
              ) : null}

              {tab === "escala" ? (
                <EmployeeScheduleTab token={token} employee={employee} onOpenContract={() => setTab("contrato")} />
              ) : null}

              {tab === "ponto" ? (
                <EmployeeTimekeepingTab token={token} employeeId={employee.id} employeeName={employee.socialName || employee.fullName} />
              ) : null}

              {tab === "ferias" ? <EmployeeVacationsTab token={token} employee={employee} onChanged={load} /> : null}

              {tab === "provisoes" ? <EmployeeProvisionsTab token={token} employee={employee} /> : null}

              {tab === "afastamentos" ? (
                <EmployeeLeavesTab token={token} employee={employee} isDp={isDpFromToken(token)} onChanged={load} />
              ) : null}

              {tab === "acesso" ? (
                <section className="dpSection">
                  <h2 className="dpSection__title">
                    Acesso ao app <CredentialStatusBadge credential={credential} />
                  </h2>

                  <p className="dpNotice">
                    O acesso do colaborador é uma conta à parte da conta de usuário do sistema:
                    ele entra pelo portal <strong>/colaborador</strong>, com CPF e senha próprios,
                    e não enxerga nenhuma tela interna. É por aqui que ele vai marcar o ponto.
                    <br />
                    <strong>Não há envio de senha por e-mail neste sistema</strong> — a senha
                    provisória aparece uma única vez nesta tela e é você quem a entrega ao
                    colaborador.
                  </p>

                  {/*
                    Os dados que o colaborador precisa para entrar. Ficam em bloco
                    próprio e sempre visíveis porque a pergunta "qual é o usuário
                    dele?" não tinha resposta na tela: o identificador é o CPF, e
                    isso estava só implícito no texto acima.
                  */}
                  <div className="dpSection" style={{ marginBottom: 24 }}>
                    <h3 className="dpSection__title">Como o colaborador entra</h3>
                    <div className="dpDefinitionGrid">
                      <Field
                        label="Endereço do portal"
                        value={
                          <CopiableValue
                            value={portalUrlExibida()}
                            copyValue={portalUrlCopiada()}
                            rotulo="Endereço"
                          />
                        }
                      />
                      <Field
                        label="Usuário (é o CPF)"
                        value={<CopiableValue value={formatCpf(employee.cpf)} rotulo="CPF" />}
                      />
                      <Field
                        label="Senha"
                        value={
                          credential?.exists
                            ? credential.isFirstLogin
                              ? "A provisória entregue pelo DP (troca obrigatória no 1º acesso)"
                              : "Definida pelo próprio colaborador"
                            : "—"
                        }
                      />
                    </div>
                  </div>

                  {credentialLoading ? (
                    <div className="dpEmpty">Carregando...</div>
                  ) : !credential?.exists ? (
                    <>
                      <div className="dpEmpty">
                        Este colaborador ainda não tem acesso ao app.
                      </div>
                      <button
                        className="dpBtn dpBtn--primary"
                        disabled={saving || employee.contracts.length === 0}
                        onClick={() => {
                          setProvisionalInput("");
                          setPasswordModal("provision");
                        }}
                      >
                        Criar acesso
                      </button>
                      {employee.contracts.length === 0 ? (
                        <p className="dpFormHint" style={{ marginTop: 8 }}>
                          Registre a admissão antes: o acesso existe para o colaborador marcar
                          ponto desde o primeiro dia.
                        </p>
                      ) : null}
                    </>
                  ) : (
                    <>
                      <div className="dpDefinitionGrid" style={{ marginBottom: 16 }}>
                        <Field
                          label="Primeiro acesso"
                          value={credential.isFirstLogin ? "Pendente" : "Concluído"}
                        />
                        <Field
                          label="Último login"
                          value={formatDateTime(credential.lastLoginAt)}
                        />
                        <Field
                          label="Bloqueado até"
                          value={credential.isLocked ? formatDateTime(credential.lockedUntil) : "—"}
                        />
                        <Field
                          label="Tentativas sem sucesso"
                          value={String(credential.failedAttempts ?? 0)}
                        />
                        <Field
                          label="Desativado em"
                          value={formatDateTime(credential.disabledAt)}
                        />
                        <Field label="Motivo" value={orDash(credential.disabledReason)} />
                      </div>

                      <div className="dpHeader__actions" style={{ marginBottom: 24 }}>
                        <button
                          className="dpBtn dpBtn--secondary"
                          disabled={saving}
                          onClick={() => {
                            setProvisionalInput("");
                            setPasswordModal("reset");
                          }}
                        >
                          Reemitir senha
                        </button>

                        {credential.disabledAt ? (
                          <button
                            className="dpBtn dpBtn--primary"
                            disabled={saving}
                            onClick={() =>
                              runCredentialAction(
                                () => reactivateCredentialApi(token!, employee.id),
                                "Acesso reativado."
                              )
                            }
                          >
                            Reativar acesso
                          </button>
                        ) : (
                          <button
                            className="dpBtn dpBtn--danger"
                            disabled={saving}
                            onClick={() => {
                              setDisableReason("");
                              setDisableModal(true);
                            }}
                          >
                            Desativar acesso
                          </button>
                        )}
                      </div>

                      <h3 className="dpSection__title">Histórico do acesso</h3>
                      {credential.events.length === 0 ? (
                        <div className="dpEmpty">Nenhum evento registrado.</div>
                      ) : (
                        <div className="dpTableWrapper">
                          <table className="dpTable">
                            <thead>
                              <tr>
                                <th>Quando</th>
                                <th>O que aconteceu</th>
                                <th>Quem</th>
                                <th>Observação</th>
                              </tr>
                            </thead>
                            <tbody>
                              {credential.events.map((evento) => (
                                <tr key={evento.id}>
                                  <td>{formatDateTime(evento.createdAt)}</td>
                                  <td>
                                    {CREDENTIAL_EVENT_LABELS[evento.type] ?? evento.type}
                                  </td>
                                  <td>
                                    {/* Sem ator = o próprio colaborador ou o sistema. */}
                                    {evento.actorUser?.nomeCompleto ?? "—"}
                                  </td>
                                  <td>{orDash(evento.note)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </>
                  )}
                </section>
              ) : null}
            </>
          )}
        </div>
      </main>

      {/* ── Modal: escolher a senha provisória ─────────────────────────────── */}
      {passwordModal && employee ? (
        <div className="dpModalOverlay">
          <div className="dpModal">
            <header className="dpModal__header">
              <h2 className="dpModal__title">
                {passwordModal === "provision" ? "Criar acesso ao app" : "Reemitir senha"}
              </h2>
              <button className="dpModal__close" onClick={() => setPasswordModal(null)}>
                &times;
              </button>
            </header>

            <div className="dpModal__body">
              <p className="dpNotice">
                Escolha uma senha simples de ditar e digitar — quem vai usá-la é o
                colaborador, no celular dele. <strong>Ela é provisória</strong>: no primeiro
                acesso o app obriga a trocar por uma senha que só ele conhece.
                {passwordModal === "reset" ? (
                  <>
                    <br />
                    Ao confirmar, a senha atual deixa de valer e o colaborador é desconectado.
                  </>
                ) : null}
              </p>

              <div className="dpFormGroup">
                <label htmlFor="provisionalPassword">Senha provisória</label>
                <input
                  id="provisionalPassword"
                  className="dpInput"
                  // Texto visível de propósito: o DP precisa LER a senha para
                  // ditá-la. Esconder atrás de bolinhas não protege nada aqui —
                  // ela vai ser exibida em claro na tela seguinte de qualquer forma.
                  type="text"
                  autoComplete="off"
                  maxLength={64}
                  placeholder="Ex.: Ambev2026"
                  value={provisionalInput}
                  onChange={(e) => setProvisionalInput(e.target.value)}
                  disabled={saving}
                />
                <span className="dpFormHint">
                  Mínimo de {MIN_PROVISIONAL_PASSWORD_LENGTH} caracteres. Não precisa de
                  maiúscula, número nem símbolo.
                </span>
              </div>
            </div>

            <footer className="dpModal__footer">
              <button
                type="button"
                className="dpBtn dpBtn--secondary"
                onClick={() => setPasswordModal(null)}
                disabled={saving}
              >
                Cancelar
              </button>

              <button
                type="button"
                className="dpBtn dpBtn--secondary"
                disabled={saving}
                // Sem senha no corpo, o backend gera uma. Mantém um único lugar
                // gerando senha (o servidor), em vez de duplicar a regra aqui.
                onClick={async () => {
                  const acao =
                    passwordModal === "provision"
                      ? () => provisionCredentialApi(token!, employee.id)
                      : () => resetCredentialApi(token!, employee.id);
                  setPasswordModal(null);
                  await runCredentialAction(
                    acao,
                    passwordModal === "provision" ? "Acesso criado." : "Nova senha gerada."
                  );
                }}
              >
                Gerar automaticamente
              </button>

              <button
                type="button"
                className="dpBtn dpBtn--primary"
                disabled={
                  saving || provisionalInput.trim().length < MIN_PROVISIONAL_PASSWORD_LENGTH
                }
                onClick={async () => {
                  const senha = provisionalInput.trim();
                  const acao =
                    passwordModal === "provision"
                      ? () => provisionCredentialApi(token!, employee.id, senha)
                      : () => resetCredentialApi(token!, employee.id, senha);
                  setPasswordModal(null);
                  await runCredentialAction(
                    acao,
                    passwordModal === "provision" ? "Acesso criado." : "Senha redefinida."
                  );
                }}
              >
                Confirmar
              </button>
            </footer>
          </div>
        </div>
      ) : null}

      {/* ── Modal: senha provisória (exibida UMA vez) ──────────────────────── */}
      {provisionalPassword ? (
        <ProvisionalPasswordModal
          password={provisionalPassword}
          cpf={employee?.cpf}
          onClose={() => setProvisionalPassword(null)}
        />
      ) : null}

      {/* ── Modal: desativar acesso ────────────────────────────────────────── */}
      {/* O modal vive fora do bloco que garante a ficha carregada — daí o teste. */}
      {disableModal && employee ? (
        <div className="dpModalOverlay">
          <div className="dpModal">
            <header className="dpModal__header">
              <h2 className="dpModal__title">Desativar acesso</h2>
              <button className="dpModal__close" onClick={() => setDisableModal(false)}>
                &times;
              </button>
            </header>

            <div className="dpModal__body">
              <p className="dpNotice">
                O colaborador é desconectado imediatamente, em todos os aparelhos, e não
                consegue mais entrar. O acesso pode ser reativado depois.
              </p>

              <div className="dpFormGroup">
                <label htmlFor="disableReason">Motivo (fica registrado no histórico)</label>
                <input
                  id="disableReason"
                  className="dpInput"
                  maxLength={200}
                  value={disableReason}
                  onChange={(e) => setDisableReason(e.target.value)}
                  disabled={saving}
                />
              </div>
            </div>

            <footer className="dpModal__footer">
              <button
                type="button"
                className="dpBtn dpBtn--secondary"
                onClick={() => setDisableModal(false)}
                disabled={saving}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="dpBtn dpBtn--danger"
                disabled={saving}
                onClick={async () => {
                  await runCredentialAction(
                    () => disableCredentialApi(token!, employee.id, disableReason.trim() || undefined),
                    "Acesso desativado."
                  );
                  setDisableModal(false);
                }}
              >
                Desativar
              </button>
            </footer>
          </div>
        </div>
      ) : null}

      {/* ── Modal: novos dependentes (vários de uma vez) ─────────────────── */}
      {dependentModal ? (
        <div className="dpModalOverlay">
          <div className="dpModal dpModal--wide">
            <header className="dpModal__header">
              <h2 className="dpModal__title">Novos dependentes</h2>
              <button className="dpModal__close" onClick={() => setDependentModal(false)}>
                &times;
              </button>
            </header>

            <form onSubmit={handleCreateDependents}>
              <div className="dpModal__body">
                <p className="dpNotice" style={{ marginBottom: 0 }}>
                  Cadastre todos os filhos de uma vez. Se algum tiver erro, nenhum é salvo e a
                  mensagem diz qual corrigir. Linhas em branco são ignoradas.
                </p>
                <DependentRowsEditor
                  rows={dependentRows}
                  onChange={setDependentRows}
                  disabled={saving}
                />
              </div>

              <footer className="dpModal__footer">
                <button
                  type="button"
                  className="dpBtn dpBtn--secondary"
                  onClick={() => setDependentModal(false)}
                  disabled={saving}
                >
                  Cancelar
                </button>
                <button type="submit" className="dpBtn dpBtn--primary" disabled={saving}>
                  Salvar
                </button>
              </footer>
            </form>
          </div>
        </div>
      ) : null}

      {/* ── Modal: editar dependente ───────────────────────────────────────── */}
      {editingDependent ? (
        <div className="dpModalOverlay">
          <div className="dpModal dpModal--wide">
            <header className="dpModal__header">
              <h2 className="dpModal__title">Editar dependente</h2>
              <button className="dpModal__close" onClick={() => setEditingDependent(null)}>
                &times;
              </button>
            </header>

            <form onSubmit={handleUpdateDependent}>
              <div className="dpModal__body">
                <DependentRowsEditor
                  rows={[editingDependent.row]}
                  onChange={(rows) =>
                    setEditingDependent((current) =>
                      current && rows[0] ? { ...current, row: rows[0] } : current
                    )
                  }
                  allowMultiple={false}
                  disabled={saving}
                />
              </div>

              <footer className="dpModal__footer">
                <button
                  type="button"
                  className="dpBtn dpBtn--secondary"
                  onClick={() => setEditingDependent(null)}
                  disabled={saving}
                >
                  Cancelar
                </button>
                <button type="submit" className="dpBtn dpBtn--primary" disabled={saving}>
                  Salvar
                </button>
              </footer>
            </form>
          </div>
        </div>
      ) : null}

      {/* ── Modal: exclusão de versão contratual (ADR 0030) ─────────────────── */}
      {versionToDelete ? (
        <ReasonModal
          title={`Excluir a versão de ${formatDateOnly(versionToDelete.version.effectiveFrom)}?`}
          description="A versão sai do histórico e a anterior volta a valer no período. Use só para lançamento errado; ela fica registrada no histórico de correções. Folha já fechada não muda."
          confirmLabel="Excluir versão"
          onClose={() => setVersionToDelete(null)}
          onConfirm={handleDeleteVersion}
        />
      ) : null}

      {/* ── Modal: opções de benefício ─────────────────────────────────────── */}
      <EcoConfirmDialog
        open={Boolean(benefitToDelete)}
        title="Excluir a opção de benefício?"
        message={
          benefitToDelete
            ? `O registro vigente a partir de ${formatDateOnly(benefitToDelete.election.effectiveFrom)} sai do histórico de vez. Use só para lançamento errado; se ele era o vigente, o anterior volta a valer. Folha já fechada não muda.`
            : ""
        }
        confirmLabel="Excluir"
        tone="danger"
        busy={saving}
        onConfirm={handleDeleteBenefit}
        onCancel={() => setBenefitToDelete(null)}
      />

      {benefitModal ? (
        <div className="dpModalOverlay">
          <div className="dpModal dpModal--wide">
            <header className="dpModal__header">
              <h2 className="dpModal__title">
                {benefitEditing ? "Corrigir opções de benefício" : "Opções de benefício"}
              </h2>
              <button className="dpModal__close" onClick={() => setBenefitModal(null)}>
                &times;
              </button>
            </header>

            <form onSubmit={handleSaveBenefit}>
              <div className="dpModal__body">
                <p className="dpNotice" style={{ marginBottom: 0 }}>
                  {benefitEditing
                    ? "Correção de lançamento errado: salvar altera este registro, sem criar outro. Folha já fechada não muda; a correção entra no próximo cálculo."
                    : "Vem preenchido com o registro vigente. Salvar cria um registro novo a partir da data informada — o anterior fica no histórico, sem alteração."}{" "}
                  VT e VR são valores por dia trabalhado; faltas descontam no fechamento do mês.
                </p>

                <div className="dpFormGroup">
                  <label htmlFor="benefitEffectiveFrom">Vigente a partir de *</label>
                  <input
                    id="benefitEffectiveFrom"
                    type="date"
                    className="dpInput"
                    value={benefitForm.effectiveFrom}
                    onChange={(e) => setBenefitForm((f) => ({ ...f, effectiveFrom: e.target.value }))}
                    disabled={saving}
                  />
                  <span className="dpFormHint">
                    {benefitEditing
                      ? "Não anterior à admissão nem igual à de outro registro."
                      : "Posterior ao último registro e não anterior à admissão."}
                  </span>
                </div>

                <fieldset className="dpBenefitBlock" disabled={saving}>
                  <label className="dpCheckboxRow">
                    <input
                      type="checkbox"
                      checked={benefitForm.salaryAdvanceOptIn}
                      onChange={(e) =>
                        setBenefitForm((f) => ({ ...f, salaryAdvanceOptIn: e.target.checked }))
                      }
                    />{" "}
                    <strong>Optante do adiantamento salarial</strong>
                  </label>
                  {benefitForm.salaryAdvanceOptIn ? (
                    <div className="dpFormGrid">
                      <div className="dpFormGroup">
                        <label htmlFor="salaryAdvancePercent">Percentual do salário (%) *</label>
                        <input
                          id="salaryAdvancePercent"
                          type="number"
                          step="0.01"
                          min="0.01"
                          max="100"
                          className="dpInput"
                          value={benefitForm.salaryAdvancePercent}
                          onChange={(e) =>
                            setBenefitForm((f) => ({ ...f, salaryAdvancePercent: e.target.value }))
                          }
                        />
                      </div>
                    </div>
                  ) : null}
                </fieldset>

                <fieldset className="dpBenefitBlock" disabled={saving}>
                  <label className="dpCheckboxRow">
                    <input
                      type="checkbox"
                      checked={benefitForm.transportVoucherOptIn}
                      onChange={(e) =>
                        setBenefitForm((f) => ({ ...f, transportVoucherOptIn: e.target.checked }))
                      }
                    />{" "}
                    <strong>Optante do vale-transporte</strong>
                  </label>
                  {benefitForm.transportVoucherOptIn ? (
                    <div className="dpFormGrid">
                      <div className="dpFormGroup">
                        <label htmlFor="vtDaily">Valor por dia trabalhado (R$) *</label>
                        <input
                          id="vtDaily"
                          type="number"
                          step="0.01"
                          min="0.01"
                          className="dpInput"
                          value={benefitForm.transportVoucherDailyAmount}
                          onChange={(e) =>
                            setBenefitForm((f) => ({ ...f, transportVoucherDailyAmount: e.target.value }))
                          }
                        />
                      </div>
                      <div className="dpFormGroup">
                        <label htmlFor="vtPct">Participação do empregado (% do salário)</label>
                        <input
                          id="vtPct"
                          type="number"
                          step="0.01"
                          min="0"
                          max={MAX_TRANSPORT_VOUCHER_EMPLOYEE_PCT}
                          className="dpInput"
                          value={benefitForm.transportVoucherEmployeePct}
                          onChange={(e) =>
                            setBenefitForm((f) => ({ ...f, transportVoucherEmployeePct: e.target.value }))
                          }
                        />
                        <span className="dpFormHint">
                          Máximo de {MAX_TRANSPORT_VOUCHER_EMPLOYEE_PCT}% do salário base (Lei 7.418/85).
                        </span>
                      </div>
                    </div>
                  ) : null}
                </fieldset>

                <fieldset className="dpBenefitBlock" disabled={saving}>
                  <label className="dpCheckboxRow">
                    <input
                      type="checkbox"
                      checked={benefitForm.mealVoucherOptIn}
                      onChange={(e) =>
                        setBenefitForm((f) => ({ ...f, mealVoucherOptIn: e.target.checked }))
                      }
                    />{" "}
                    <strong>Optante do vale-refeição</strong>
                  </label>
                  {benefitForm.mealVoucherOptIn ? (
                    <div className="dpFormGrid">
                      <div className="dpFormGroup">
                        <label htmlFor="vrDaily">Valor por dia trabalhado (R$) *</label>
                        <input
                          id="vrDaily"
                          type="number"
                          step="0.01"
                          min="0.01"
                          className="dpInput"
                          value={benefitForm.mealVoucherDailyAmount}
                          onChange={(e) =>
                            setBenefitForm((f) => ({ ...f, mealVoucherDailyAmount: e.target.value }))
                          }
                        />
                      </div>
                      <div className="dpFormGroup">
                        <label htmlFor="vrPct">Participação do empregado (% do benefício)</label>
                        <input
                          id="vrPct"
                          type="number"
                          step="0.01"
                          min="0"
                          max="100"
                          className="dpInput"
                          value={benefitForm.mealVoucherEmployeePct}
                          onChange={(e) =>
                            setBenefitForm((f) => ({ ...f, mealVoucherEmployeePct: e.target.value }))
                          }
                        />
                        <span className="dpFormHint">No PAT, costuma ser de até 20%.</span>
                      </div>
                    </div>
                  ) : null}
                </fieldset>

                <fieldset className="dpBenefitBlock" disabled={saving}>
                  <label className="dpCheckboxRow">
                    <input
                      type="checkbox"
                      checked={benefitForm.foodVoucherOptIn}
                      onChange={(e) =>
                        setBenefitForm((f) => ({ ...f, foodVoucherOptIn: e.target.checked }))
                      }
                    />{" "}
                    <strong>Optante do vale-alimentação</strong>
                  </label>
                  {benefitForm.foodVoucherOptIn ? (
                    <div className="dpFormGrid">
                      <div className="dpFormGroup">
                        <label htmlFor="vaMode">Modalidade *</label>
                        <select
                          id="vaMode"
                          className="dpSelect"
                          value={benefitForm.foodVoucherMode}
                          onChange={(e) =>
                            setBenefitForm((f) => ({ ...f, foodVoucherMode: e.target.value as "POR_DIA" | "MENSAL" }))
                          }
                        >
                          <option value="POR_DIA">Por dia trabalhado</option>
                          <option value="MENSAL">Valor mensal fixo</option>
                        </select>
                      </div>
                      <div className="dpFormGroup">
                        <label htmlFor="vaAmount">
                          {benefitForm.foodVoucherMode === "MENSAL" ? "Valor mensal (R$) *" : "Valor por dia trabalhado (R$) *"}
                        </label>
                        <input
                          id="vaAmount"
                          type="number"
                          step="0.01"
                          min="0.01"
                          className="dpInput"
                          value={benefitForm.foodVoucherAmount}
                          onChange={(e) =>
                            setBenefitForm((f) => ({ ...f, foodVoucherAmount: e.target.value }))
                          }
                        />
                      </div>
                      <div className="dpFormGroup">
                        <label htmlFor="vaPct">Participação do empregado (% do benefício)</label>
                        <input
                          id="vaPct"
                          type="number"
                          step="0.01"
                          min="0"
                          max="100"
                          className="dpInput"
                          value={benefitForm.foodVoucherEmployeePct}
                          onChange={(e) =>
                            setBenefitForm((f) => ({ ...f, foodVoucherEmployeePct: e.target.value }))
                          }
                        />
                        <span className="dpFormHint">
                          Use só se o VA for separado do VR. Na Eco, hoje VR e VA são um benefício só.
                        </span>
                      </div>
                    </div>
                  ) : null}
                </fieldset>

                <fieldset className="dpBenefitBlock" disabled={saving}>
                  <label className="dpCheckboxRow">
                    <input
                      type="checkbox"
                      checked={benefitForm.fuelVoucherOptIn}
                      onChange={(e) =>
                        setBenefitForm((f) => ({ ...f, fuelVoucherOptIn: e.target.checked }))
                      }
                    />{" "}
                    <strong>Optante do vale-combustível</strong>
                  </label>
                  {benefitForm.fuelVoucherOptIn ? (
                    <div className="dpFormGrid">
                      <div className="dpFormGroup">
                        <label htmlFor="fuelMode">Modalidade *</label>
                        <select
                          id="fuelMode"
                          className="dpSelect"
                          value={benefitForm.fuelVoucherMode}
                          onChange={(e) =>
                            setBenefitForm((f) => ({ ...f, fuelVoucherMode: e.target.value as "POR_DIA" | "MENSAL" }))
                          }
                        >
                          <option value="POR_DIA">Por dia trabalhado</option>
                          <option value="MENSAL">Valor mensal fixo</option>
                        </select>
                      </div>
                      <div className="dpFormGroup">
                        <label htmlFor="fuelAmount">
                          {benefitForm.fuelVoucherMode === "MENSAL" ? "Valor mensal (R$) *" : "Valor por dia trabalhado (R$) *"}
                        </label>
                        <input
                          id="fuelAmount"
                          type="number"
                          step="0.01"
                          min="0.01"
                          className="dpInput"
                          value={benefitForm.fuelVoucherAmount}
                          onChange={(e) =>
                            setBenefitForm((f) => ({ ...f, fuelVoucherAmount: e.target.value }))
                          }
                        />
                        <span className="dpFormHint">Pago na folha, sem INSS, IR e FGTS e fora do adiantamento.</span>
                      </div>
                    </div>
                  ) : null}
                </fieldset>

                <fieldset className="dpBenefitBlock" disabled={saving}>
                  <label className="dpCheckboxRow">
                    <input
                      type="checkbox"
                      checked={benefitForm.motorcycleRentalOptIn}
                      onChange={(e) =>
                        setBenefitForm((f) => ({ ...f, motorcycleRentalOptIn: e.target.checked }))
                      }
                    />{" "}
                    <strong>Aluguel de moto</strong>
                  </label>
                  {benefitForm.motorcycleRentalOptIn ? (
                    <div className="dpFormGrid">
                      <div className="dpFormGroup">
                        <label htmlFor="motorcycleRentalAmount">Valor mensal (R$) *</label>
                        <input
                          id="motorcycleRentalAmount"
                          type="number"
                          step="0.01"
                          min="0.01"
                          className="dpInput"
                          value={benefitForm.motorcycleRentalAmount}
                          onChange={(e) =>
                            setBenefitForm((f) => ({ ...f, motorcycleRentalAmount: e.target.value }))
                          }
                        />
                        <span className="dpFormHint">Pago na folha, sem INSS, IR e FGTS e fora do adiantamento.</span>
                      </div>
                    </div>
                  ) : null}
                </fieldset>

                <div className="dpFormGroup">
                  <label htmlFor="benefitNotes">Observações</label>
                  <textarea
                    id="benefitNotes"
                    className="dpTextarea"
                    maxLength={1000}
                    value={benefitForm.notes}
                    onChange={(e) => setBenefitForm((f) => ({ ...f, notes: e.target.value }))}
                    disabled={saving}
                  />
                </div>
              </div>

              <footer className="dpModal__footer">
                <button
                  type="button"
                  className="dpBtn dpBtn--secondary"
                  onClick={() => setBenefitModal(null)}
                  disabled={saving}
                >
                  Cancelar
                </button>
                <button type="submit" className="dpBtn dpBtn--primary" disabled={saving}>
                  {benefitEditing ? "Salvar correção" : "Registrar"}
                </button>
              </footer>
            </form>
          </div>
        </div>
      ) : null}

      {terminationContractId && token && employee ? (
        <TerminationPaymentModal
          token={token}
          contractId={terminationContractId}
          employeeName={employee.fullName}
          onClose={() => setTerminationContractId(null)}
        />
      ) : null}

      {probationContract && token ? (
        <ProbationEditModal
          token={token}
          contract={probationContract}
          onClose={() => setProbationContract(null)}
          onSaved={() => {
            setProbationContract(null);
            load();
          }}
        />
      ) : null}

      {/* ── Modal: admissão ────────────────────────────────────────────────── */}
      {contractModal ? (
        <div className="dpModalOverlay">
          <div className="dpModal dpModal--wide">
            <header className="dpModal__header">
              <h2 className="dpModal__title">Registrar admissão</h2>
              <button className="dpModal__close" onClick={() => setContractModal(false)}>
                &times;
              </button>
            </header>

            <form onSubmit={handleCreateContract}>
              <div className="dpModal__body">
                <div className="dpFormGroup">
                  <label htmlFor="employerId">Empregador *</label>
                  <select
                    id="employerId"
                    className="dpSelect"
                    value={contractForm.employerId}
                    onChange={(e) =>
                      setContractForm((f) => ({ ...f, employerId: e.target.value }))
                    }
                    disabled={saving}
                  >
                    <option value="">Selecione...</option>
                    {employers
                      .filter((e) => e.isActive)
                      .map((e) => (
                        <option key={e.id} value={e.id}>
                          {e.tradeName || e.legalName}
                        </option>
                      ))}
                  </select>
                </div>

                <div className="dpFormGroup">
                  <label htmlFor="contractType">Tipo de contrato *</label>
                  <select
                    id="contractType"
                    className="dpSelect"
                    value={contractForm.contractType}
                    onChange={(e) =>
                      setContractForm((f) => ({
                        ...f,
                        contractType: e.target.value as DpContractType,
                        // Contrato sem prazo não aceita data de término: o backend recusa.
                        fixedTermEndDate: CONTRACT_TYPES_WITH_FIXED_TERM.includes(
                          e.target.value as DpContractType
                        )
                          ? f.fixedTermEndDate
                          : "",
                      }))
                    }
                    disabled={saving}
                  >
                    {(Object.keys(CONTRACT_TYPE_LABELS) as DpContractType[]).map((t) => (
                      <option key={t} value={t}>
                        {CONTRACT_TYPE_LABELS[t]}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="dpFormGroup">
                  <label htmlFor="admissionDate">Data de admissão *</label>
                  <input
                    id="admissionDate"
                    type="date"
                    className="dpInput"
                    value={contractForm.admissionDate}
                    onChange={(e) =>
                      setContractForm((f) => ({ ...f, admissionDate: e.target.value }))
                    }
                    disabled={saving}
                  />
                  <span className="dpFormHint">
                    A versão inicial do contrato passa a valer nesta mesma data.
                  </span>
                </div>

                <div className="dpFormGroup">
                  <label htmlFor="probationFirstEndDate">Fim do 1º período da experiência</label>
                  <input
                    id="probationFirstEndDate"
                    type="date"
                    className="dpInput"
                    value={contractForm.probationFirstEndDate}
                    onChange={(e) =>
                      setContractForm((f) => ({ ...f, probationFirstEndDate: e.target.value }))
                    }
                    disabled={saving}
                  />
                  <button
                    type="button"
                    className="dpBtn dpBtn--secondary dpBtn--small"
                    style={{ marginTop: 6, alignSelf: "flex-start" }}
                    disabled={saving || !contractForm.admissionDate}
                    onClick={() =>
                      setContractForm((f) => ({ ...f, ...probation45Plus45(f.admissionDate) }))
                    }
                  >
                    Preencher 45 + 45
                  </button>
                </div>

                <div className="dpFormGroup">
                  <label htmlFor="probationEndDate">Fim da experiência</label>
                  <input
                    id="probationEndDate"
                    type="date"
                    className="dpInput"
                    value={contractForm.probationEndDate}
                    onChange={(e) =>
                      setContractForm((f) => ({ ...f, probationEndDate: e.target.value }))
                    }
                    disabled={saving}
                  />
                  <span className="dpFormHint">No máximo 90 dias, contando o dia da admissão.</span>
                </div>

                {CONTRACT_TYPES_WITH_FIXED_TERM.includes(contractForm.contractType) ? (
                  <div className="dpFormGroup">
                    <label htmlFor="fixedTermEndDate">Término do contrato *</label>
                    <input
                      id="fixedTermEndDate"
                      type="date"
                      className="dpInput"
                      value={contractForm.fixedTermEndDate}
                      onChange={(e) =>
                        setContractForm((f) => ({ ...f, fixedTermEndDate: e.target.value }))
                      }
                      disabled={saving}
                    />
                  </div>
                ) : null}

                <div className="dpFormGroup">
                  <label htmlFor="registrationNumber">Matrícula</label>
                  <input
                    id="registrationNumber"
                    className="dpInput"
                    value={contractForm.registrationNumber}
                    onChange={(e) =>
                      setContractForm((f) => ({ ...f, registrationNumber: e.target.value }))
                    }
                    disabled={saving}
                  />
                </div>

                <hr style={{ border: 0, borderTop: "1px solid #e2e8f0" }} />
                <h3 className="dpSection__title" style={{ marginBottom: 0 }}>
                  Condições iniciais
                </h3>

                {versionFields(initialVersionForm, setInitialVersionForm, {
                  showReason: false,
                  showEffectiveFrom: false,
                })}
              </div>

              <footer className="dpModal__footer">
                <button
                  type="button"
                  className="dpBtn dpBtn--secondary"
                  onClick={() => setContractModal(false)}
                  disabled={saving}
                >
                  Cancelar
                </button>
                <button type="submit" className="dpBtn dpBtn--primary" disabled={saving}>
                  Registrar admissão
                </button>
              </footer>
            </form>
          </div>
        </div>
      ) : null}

      {/* ── Modal: alteração contratual ────────────────────────────────────── */}
      {versionModal ? (
        <div className="dpModalOverlay">
          <div className="dpModal dpModal--wide">
            <header className="dpModal__header">
              <h2 className="dpModal__title">
                {versionEditing ? "Corrigir versão contratual" : "Nova alteração contratual"}
              </h2>
              <button className="dpModal__close" onClick={closeVersionModal}>
                &times;
              </button>
            </header>

            <form onSubmit={handleAddVersion}>
              <div className="dpModal__body">
                <p className="dpNotice" style={{ marginBottom: 0 }}>
                  {versionEditing
                    ? `Correção da versão vigente a partir de ${formatDateOnly(versionEditing.effectiveFrom)}. Use para lançamento errado: a versão de antes fica no histórico de correções. Folha já fechada não muda.`
                    : "Os campos vêm preenchidos com a versão vigente — altere apenas o que muda."}
                </p>
                {versionFields(versionForm, setVersionForm, {
                  showReason: true,
                  showEffectiveFrom: true,
                  lockAdmission: versionEditing?.changeReason === "ADMISSAO",
                  editing: Boolean(versionEditing),
                })}
                {versionEditing ? (
                  <>
                    <div className="dpFormGroup">
                      <label htmlFor="versionEditReason">Motivo da correção *</label>
                      <textarea
                        id="versionEditReason"
                        className="dpTextarea"
                        maxLength={500}
                        value={versionEditReason}
                        onChange={(e) => setVersionEditReason(e.target.value)}
                        disabled={saving}
                      />
                    </div>
                    <VersionChangesHistory changes={versionChanges} />
                  </>
                ) : null}
              </div>

              <footer className="dpModal__footer">
                <button
                  type="button"
                  className="dpBtn dpBtn--secondary"
                  onClick={closeVersionModal}
                  disabled={saving}
                >
                  Cancelar
                </button>
                <button type="submit" className="dpBtn dpBtn--primary" disabled={saving}>
                  {versionEditing ? "Salvar correção" : "Registrar alteração"}
                </button>
              </footer>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** Rastro das correções e exclusões de versão do contrato (ADR 0030), do mais recente. */
const VersionChangesHistory: React.FC<{ changes: DpContractVersionChange[] | null }> = ({ changes }) => (
  <div className="dpFormGroup">
    <label>Histórico de correções do contrato</label>
    {changes === null ? (
      <span className="dpFormHint">Carregando...</span>
    ) : changes.length === 0 ? (
      <span className="dpFormHint">Nenhuma correção registrada.</span>
    ) : (
      <ul style={{ margin: 0, paddingLeft: 18, display: "flex", flexDirection: "column", gap: 6 }}>
        {changes.map((c) => (
          <li key={c.id} className="dpFormHint">
            {new Date(c.createdAt).toLocaleString("pt-BR")} · {c.createdBy?.nomeCompleto ?? "—"} ·{" "}
            {c.action === "EXCLUIDA" ? "excluiu" : "corrigiu"} a versão de{" "}
            {formatDateOnly(c.previousSnapshot.effectiveFrom ?? null)}
            {c.previousSnapshot.salaryAmount
              ? ` (salário antes: ${formatCurrency(c.previousSnapshot.salaryAmount)})`
              : ""}
            . Motivo: {c.reason}
          </li>
        ))}
      </ul>
    )}
  </div>
);

/** Adicionais de uma versão, como estão no contrato. */
const AllowancesSummary: React.FC<{ version: DpContractVersion }> = ({ version: v }) => (
  <>
    Insalubridade: {describeAllowance(v.insalubrityPercent, v.insalubrityBase, v.insalubrityBaseAmount)}
    <br />
    Periculosidade: {describeAllowance(v.hazardPercent, v.hazardBase, v.hazardBaseAmount)}
    <br />
    Atividade complementar: {describeComplementaryActivity(v.complementaryActivityPercent)}
  </>
);

type AllowanceValue = NonNullable<DpRemunerationSummary["contract"]>["insalubrity"];

const allowanceValue = (a: AllowanceValue): string => {
  if (!a) return "não informado";
  if (Number(a.percent) === 0) return "não recebe";
  if (a.amount === null) return `${formatPercent(a.percent)} — sem valor (salário mínimo não cadastrado)`;
  const base = a.base ? ` s/ ${ALLOWANCE_BASE_SHORT_LABELS[a.base]}` : "";
  return `${formatCurrency(a.amount)} (${formatPercent(a.percent)}${base})`;
};

/**
 * "Mês cheio" do contrato vigente, calculado no backend. É o que a planilha do DP
 * mostrava — sem proporcionais, faltas nem descontos, que são do fechamento.
 */
const RemunerationCard: React.FC<{ summary: DpRemunerationSummary }> = ({ summary }) => {
  const c = summary.contract!;
  const notInformed = c.benefitElectionId === null;
  const benefit = (value: string | null) => value ?? (notInformed ? "não informado" : "não optante");

  return (
    <div className="dpSection" style={{ marginBottom: 16 }}>
      <h3 className="dpSection__title">Remuneração de referência</h3>
      {summary.warnings.map((warning) => (
        <p key={warning} className="dpNotice dpNotice--warning">
          {warning}
        </p>
      ))}
      <div className="dpDefinitionGrid">
        <Field label="Salário mensal" value={formatCurrency(c.baseSalaryMonthly)} />
        <Field label="Insalubridade" value={allowanceValue(c.insalubrity)} />
        <Field label="Periculosidade" value={allowanceValue(c.hazard)} />
        <Field label="Atividade complementar" value={allowanceValue(c.complementaryActivity)} />
        <Field
          label="Remuneração de referência"
          value={c.referenceRemuneration ? formatCurrency(c.referenceRemuneration) : "indefinida"}
        />
        <Field
          label="Adiantamento"
          value={benefit(
            c.salaryAdvance
              ? `${formatCurrency(c.salaryAdvance.amount)} (${formatPercent(c.salaryAdvance.percent)})`
              : null
          )}
        />
        <Field
          label="Vale-transporte"
          value={benefit(
            c.transportVoucher
              ? describeVoucher(true, c.transportVoucher.dailyAmount, c.transportVoucher.employeePct)
              : null
          )}
        />
        <Field
          label="Vale-refeição"
          value={benefit(
            c.mealVoucher
              ? describeVoucher(true, c.mealVoucher.dailyAmount, c.mealVoucher.employeePct)
              : null
          )}
        />
      </div>
      <p className="dpFormHint" style={{ marginTop: 12 }}>
        Valores de referência do mês cheio. Proporcionais, faltas e descontos são apurados no
        fechamento.
      </p>
    </div>
  );
};

/** Histórico das opções de benefício do contrato (o vigente em destaque). */
const BenefitsCard: React.FC<{
  elections: DpBenefitElection[];
  onEdit: (() => void) | null;
  onEditRow: (election: DpBenefitElection) => void;
  onDeleteRow: (election: DpBenefitElection) => void;
}> = ({ elections, onEdit, onEditRow, onDeleteRow }) => (
  <div className="dpSection" style={{ marginBottom: 16 }}>
    <div className="dpHeader" style={{ marginBottom: 12 }}>
      <h3 className="dpSection__title" style={{ margin: 0 }}>
        Benefícios
      </h3>
      {onEdit ? (
        <button className="dpBtn dpBtn--secondary" onClick={onEdit}>
          Alterar opções de benefício
        </button>
      ) : null}
    </div>

    {elections.length === 0 ? (
      <p className="dpFormHint">
        Nenhuma opção registrada — adiantamento, VT, VR e VA aparecem como "não informado".
      </p>
    ) : (
      <div className="dpTableWrapper">
        <table className="dpTable">
          <thead>
            <tr>
              <th>Vigência</th>
              <th>Adiantamento</th>
              <th>Vale-transporte</th>
              <th>Vale-refeição</th>
              <th>Vale-alimentação</th>
              <th>Vale-combustível</th>
              <th>Aluguel de moto</th>
              <th style={{ width: 170 }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {elections.map((e) => (
              <tr key={e.id}>
                <td>
                  {formatDateOnly(e.effectiveFrom)} —{" "}
                  {e.effectiveTo ? formatDateOnly(e.effectiveTo) : "vigente"}
                </td>
                <td>{describeAdvance(e)}</td>
                <td>
                  {describeVoucher(
                    e.transportVoucherOptIn,
                    e.transportVoucherDailyAmount,
                    e.transportVoucherEmployeePct
                  )}
                </td>
                <td>
                  {describeVoucher(e.mealVoucherOptIn, e.mealVoucherDailyAmount, e.mealVoucherEmployeePct)}
                </td>
                <td>{describeFoodVoucher(e)}</td>
                <td>{describeFuelVoucher(e)}</td>
                <td>{describeMotorcycleRental(e)}</td>
                <td>
                  <div className="dpTable__actions">
                    <button
                      className="dpBtn dpBtn--secondary"
                      style={{ height: 30, padding: "0 10px" }}
                      onClick={() => onEditRow(e)}
                    >
                      Editar
                    </button>
                    <button
                      className="dpBtn dpBtn--danger"
                      style={{ height: 30, padding: "0 10px" }}
                      onClick={() => onDeleteRow(e)}
                    >
                      Excluir
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

type SalarioFamiliaEvaluation = DpRemunerationSummary["salarioFamilia"]["dependents"][number];

/** Direito calculado ao salário-família, com o motivo e o aviso de divergência. */
const SalarioFamiliaCell: React.FC<{ evaluation?: SalarioFamiliaEvaluation }> = ({ evaluation }) => {
  if (!evaluation) return <>—</>;

  const reasons = evaluation.reasons.map((r) => SALARIO_FAMILIA_REASON_LABELS[r]).join("; ");
  const badge =
    evaluation.eligible === true ? (
      <span className="dpBadge dpBadge--green">Tem direito</span>
    ) : evaluation.eligible === false ? (
      <span className="dpBadge dpBadge--gray" title={reasons}>
        Sem direito
      </span>
    ) : (
      <span className="dpBadge dpBadge--orange" title={reasons}>
        Indeterminado
      </span>
    );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      {badge}
      {reasons ? <span className="dpFormHint">{reasons}</span> : null}
      {evaluation.divergent ? (
        <span className="dpFormHint" style={{ color: "#8a4b08" }}>
          ⚠ Diverge da marcação manual
        </span>
      ) : null}
    </div>
  );
};

const Field: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div>
    <div className="dpDefinition__label">{label}</div>
    <div className="dpDefinition__value">{value}</div>
  </div>
);

/**
 * Valor com botão de copiar — para os dados que o DP repassa ao colaborador
 * (endereço do portal e CPF). Digitar uma URL ou um CPF à mão num bilhete é
 * onde nascem os "não consigo entrar".
 */
const CopiableValue: React.FC<{ value: string; copyValue?: string; rotulo: string }> = ({
  value,
  copyValue,
  rotulo,
}) => (
  <span style={{ display: "inline-flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
    <span style={{ wordBreak: "break-all" }}>{value}</span>
    <button
      type="button"
      className="dpBtn dpBtn--secondary"
      style={{ height: 28, padding: "0 10px", fontSize: 12 }}
      onClick={() => {
        // `clipboard` não existe em contexto não seguro (http sem localhost);
        // nesse caso o valor continua visível ao lado para cópia manual.
        navigator.clipboard
          ?.writeText(copyValue ?? value)
          .then(() => toast.success(`${rotulo} copiado.`))
          .catch(() => toast.info(`Copie o ${rotulo.toLowerCase()} manualmente.`));
      }}
    >
      Copiar
    </button>
  </span>
);
